<script setup>
import { ref, computed, onMounted, watch } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

import FlowJsonPreview from './FlowJsonPreview.vue';
import { getFlowSummary, parseFlowJson } from './flowJsonSchema';
import {
  BUTTON_TYPE_LIMITS,
  MAX_BUTTON_LABEL_LENGTH,
  MAX_TEMPLATE_BUTTONS,
  canAddButtonType,
  validateTemplateButtons,
} from './templateButtonRules';

import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { uploadFile } from 'dashboard/helper/uploadHelper';

const props = defineProps({
  template: { type: Object, default: () => null },
  context: { type: String, default: 'campaign' }, // 'campaign' or 'active_messages'
});

const emit = defineEmits(['close']);

const store = useStore();
const { t } = useI18n();

const dialogRef = ref(null);
const defaultTokenCode = '{{codigo}}';
const normalizeVariableSyntax = value =>
  String(value || '').replace(/\{\{\s*([^{}]+?)\s*\}\}/g, '{{$1}}');
const PHONE_COUNTRIES = [
  { code: '+55', nameKey: 'ACTIVE_MESSAGES.COUNTRIES.BRAZIL' },
  { code: '+1', nameKey: 'ACTIVE_MESSAGES.COUNTRIES.US_CANADA' },
  { code: '+351', nameKey: 'ACTIVE_MESSAGES.COUNTRIES.PORTUGAL' },
  { code: '+34', nameKey: 'ACTIVE_MESSAGES.COUNTRIES.SPAIN' },
  { code: '+44', nameKey: 'ACTIVE_MESSAGES.COUNTRIES.UNITED_KINGDOM' },
];

const extractPhoneParts = value => {
  const normalized = String(value || '').replace(/\D/g, '');
  const country = PHONE_COUNTRIES.slice()
    .sort((first, second) => second.code.length - first.code.length)
    .find(item => normalized.startsWith(item.code.slice(1)));
  const countryCode = country?.code || '+55';
  return {
    countryCode,
    phoneLocal: normalized.startsWith(countryCode.slice(1))
      ? normalized.slice(countryCode.length - 1)
      : normalized,
  };
};

const prepareButton = button => ({
  ...button,
  actionType: button.payment_method ? 'payment' : button.type,
  ...(button.type === 'phone' ? extractPhoneParts(button.url) : {}),
});

const form = ref({
  name: props.template?.name || '',
  category: props.template?.category || 'marketing',
  content: props.template?.content || '',
  inbox_id: props.template?.inbox_id || null,
  team_id: props.template?.team_id || null,
  buttons: (props.template?.buttons || []).map(prepareButton),
  footer: props.template?.variables?.footer || '',
  useCarousel: props.template?.variables?.useCarousel || false,
  carouselItems: props.template?.variables?.carouselItems || [],
  // Mantidos por compatibilidade com modelos antigos. A interface não promete
  // callbacks de entrega enquanto esse processamento não existir no backend.
  useWebhook: props.template?.variables?.useWebhook || false,
  webhookUrl: props.template?.variables?.webhookUrl || '',
  useHeader: props.template?.variables?.useHeader || false,
  headerType: props.template?.variables?.headerType || 'image',
  headerUrl: props.template?.variables?.headerUrl || '',
  allowed_in_chat:
    props.template?.variables?.allowed_in_chat === true ||
    props.template?.variables?.allowed_in_chat === 'true',
  format: 'simple',
  couponCode: '',
  tokenCode: defaultTokenCode,
  codeExpirationMinutes: 10,
  catalogId: '',
  flowId: '',
  flowName: '',
  flowCta: t('ACTIVE_MESSAGES.OPEN'),
  flowScreen: '',
  flowToken: '{{flow_token}}',
  flowJson: null,
  flowJsonFileName: '',
  listButtonText: props.template?.variables?.listButtonText || '',
  listItems: props.template?.variables?.listItems || [],
  paymentDetails: props.template?.variables?.payment_details === true,
  paymentTitle:
    props.template?.variables?.payment_title ||
    t('ACTIVE_MESSAGES.PAYMENT_DEFAULT_TITLE'),
  paymentProduct:
    props.template?.variables?.payment_product ||
    t('ACTIVE_MESSAGES.PAYMENT_DEFAULT_PRODUCT'),
  paymentQuantity: props.template?.variables?.payment_quantity || '1',
  paymentMethods: props.template?.variables?.payment_methods || ['pix'],
  paymentImageUrl: props.template?.variables?.payment_image_url || '',
  paymentImageBlobId: props.template?.variables?.payment_image_blob_id || '',
});

const paymentMethodOptions = [
  { key: 'pix', label: t('ACTIVE_MESSAGES.PIX'), icon: 'i-lucide-qr-code' },
  {
    key: 'boleto',
    label: t('ACTIVE_MESSAGES.BOLETO'),
    icon: 'i-lucide-barcode',
  },
  {
    key: 'payment_link',
    label: t('ACTIVE_MESSAGES.PAYMENT_LINK'),
    icon: 'i-lucide-external-link',
  },
];
const paymentActionOptions = [
  { key: 'pix', label: t('ACTIVE_MESSAGES.PIX_CODE') },
  { key: 'boleto', label: t('ACTIVE_MESSAGES.BOLETO_CODE') },
  { key: 'payment_link', label: t('ACTIVE_MESSAGES.PAYMENT_LINK') },
];

const paymentButtonFor = method => ({
  type: 'payment_method',
  actionType: 'payment',
  payment_method: method.key,
  label: t('ACTIVE_MESSAGES.REQUEST_PAYMENT'),
  url: '',
  merchant_name: '',
  pix_key: '',
  pix_key_type: 'CNPJ',
});

const syncPaymentButtons = () => {
  const existing = new Map(
    form.value.buttons
      .filter(button => button.type === 'payment_method')
      .map(button => [button.payment_method, button])
  );
  form.value.buttons = form.value.paymentMethods.map(method => {
    const option = paymentActionOptions.find(item => item.key === method);
    return (
      existing.get(method) ||
      paymentButtonFor(option || { key: method, label: method })
    );
  });
};

const syncPaymentMethods = () => {
  form.value.paymentMethods = form.value.buttons
    .filter(button => button.type === 'payment_method')
    .map(button => button.payment_method)
    .filter(method => paymentMethodOptions.some(item => item.key === method));
};

const togglePaymentMethod = method => {
  if (form.value.format === 'payment') {
    form.value.paymentMethods = [method];
    syncPaymentButtons();
    return;
  }
  const methods = new Set(form.value.paymentMethods);
  if (methods.has(method)) methods.delete(method);
  else methods.add(method);
  form.value.paymentMethods = [...methods];
  syncPaymentButtons();
};

const addPaymentButton = () => {
  const next = paymentActionOptions.find(
    method => !form.value.paymentMethods.includes(method.key)
  );
  if (!next) return;
  form.value.paymentMethods = [...form.value.paymentMethods, next.key];
  syncPaymentButtons();
};

const removePaymentButton = index => {
  form.value.buttons.splice(index, 1);
  syncPaymentMethods();
};

const togglePaymentDetails = enabled => {
  form.value.paymentDetails = enabled;
  if (enabled) {
    form.value.format = 'payment';
    syncPaymentButtons();
  } else if (form.value.format === 'payment') {
    form.value.format = 'simple';
  }
};

const toggleMediaHeader = enabled => {
  form.value.useHeader = enabled;
};

const paymentTotalPreview = computed(() => '{{valor}}');
const paymentPrimaryAction = computed(() =>
  form.value.buttons.find(button => button.type === 'payment_method')
);
const paymentPrimaryActionLabel = computed(() => {
  const method = paymentPrimaryAction.value?.payment_method;
  if (method === 'boleto') return t('ACTIVE_MESSAGES.COPY_BOLETO_CODE');
  if (method === 'payment_link') return t('ACTIVE_MESSAGES.OPEN_PAYMENT_LINK');
  return t('ACTIVE_MESSAGES.COPY_PIX_CODE');
});

const flowJsonInput = ref(null);
const flowJsonSource = ref('');
const flowJsonError = ref('');
const isFlowPreviewOpen = ref(false);
const flowJsonSummary = computed(() => getFlowSummary(form.value.flowJson));

const applyFlowJson = (source, fileName = '') => {
  try {
    const schema = parseFlowJson(source, t);
    form.value.flowJson = schema;
    form.value.flowJsonFileName = fileName;
    flowJsonSource.value = JSON.stringify(schema, null, 2);
    flowJsonError.value = '';

    if (!schema.screens.some(screen => screen.id === form.value.flowScreen)) {
      form.value.flowScreen = schema.screens[0].id;
    }
    isFlowPreviewOpen.value = true;
    useAlert(t('ACTIVE_MESSAGES.FLOW_JSON_APPLIED'));
  } catch (error) {
    const message =
      error.message ||
      t('ACTIVE_MESSAGES.FLOW_JSON_ERRORS.INVALID_JSON_GENERIC');
    flowJsonError.value = message;
    useAlert(message);
  }
};

const handleFlowJsonUpload = async event => {
  const file = event.target.files[0];
  if (!file) return;

  try {
    applyFlowJson(await file.text(), file.name);
  } finally {
    event.target.value = '';
  }
};

// Upload Handlers
const headerUploadInput = ref(null);
const triggerHeaderUpload = () => {
  headerUploadInput.value?.click();
};
const handleHeaderUpload = async event => {
  const file = event.target.files[0];
  if (!file) return;
  try {
    const { fileUrl, blobId } = await uploadFile(file);
    form.value.headerUrl = fileUrl;
    form.value.headerBlobId = blobId;
  } catch (error) {
    useAlert(t('ACTIVE_MESSAGES.UPLOAD_ERROR'));
  } finally {
    event.target.value = '';
  }
};

const paymentImageUploadInput = ref(null);
const triggerPaymentImageUpload = () => {
  paymentImageUploadInput.value?.click();
};
const handlePaymentImageUpload = async event => {
  const file = event.target.files[0];
  if (!file) return;
  try {
    const { fileUrl, blobId } = await uploadFile(file);
    form.value.paymentImageUrl = fileUrl;
    form.value.paymentImageBlobId = blobId;
  } catch (error) {
    useAlert(t('ACTIVE_MESSAGES.PAYMENT_IMAGE_UPLOAD_ERROR'));
  } finally {
    event.target.value = '';
  }
};

const carouselUploadInput = ref(null);
const uploadingCarouselIndex = ref(null);
const triggerCarouselImageUpload = index => {
  uploadingCarouselIndex.value = index;
  carouselUploadInput.value?.click();
};
const handleCarouselImageUpload = async event => {
  const file = event.target.files[0];
  if (!file) return;
  const index = uploadingCarouselIndex.value;
  try {
    const { fileUrl, blobId } = await uploadFile(file);
    if (index !== null && form.value.carouselItems[index]) {
      form.value.carouselItems[index].imageUrl = fileUrl;
      form.value.carouselItems[index].blobId = blobId;
    }
  } catch (error) {
    useAlert(t('ACTIVE_MESSAGES.IMAGE_UPLOAD_ERROR'));
  } finally {
    event.target.value = '';
    uploadingCarouselIndex.value = null;
  }
};

const inboxes = computed(() => store.getters['inboxes/getWhatsAppInboxes']);
const teams = computed(() => store.getters['teams/getTeams'] || []);

const selectedInbox = computed(() =>
  inboxes.value.find(inbox => String(inbox.id) === String(form.value.inbox_id))
);
const isEvolutionInbox = computed(() => {
  const inbox = selectedInbox.value;
  return Boolean(
    inbox &&
      (inbox.channel_type === 'Channel::Api' ||
        inbox.channel_type === 'Channel::EvolutionWhatsapp')
  );
});
const isOfficialInbox = computed(
  () => selectedInbox.value?.channel_type === 'Channel::Whatsapp'
);
const directSendStatus = computed(() => {
  if (!selectedInbox.value) return null;
  if (!isOfficialInbox.value) return 'not_applicable';
  return selectedInbox.value.direct_send_status || 'unknown';
});
const directSendStatusLabel = computed(() => {
  const labels = {
    available: t('ACTIVE_MESSAGES.DIRECT_AVAILABLE'),
    unavailable: t('ACTIVE_MESSAGES.DIRECT_UNAVAILABLE'),
    lost_access: t('ACTIVE_MESSAGES.DIRECT_LOST_ACCESS'),
    not_applicable: t('ACTIVE_MESSAGES.DIRECT_NOT_APPLICABLE'),
    unknown: t('ACTIVE_MESSAGES.DIRECT_UNKNOWN'),
  };
  return labels[directSendStatus.value] || labels.unknown;
});
const directSendStatusClass = computed(() => {
  if (directSendStatus.value === 'available') {
    return 'text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-500/10';
  }
  if (directSendStatus.value === 'not_applicable') {
    return 'text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-700/50';
  }
  return 'text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-500/10';
});
const previewApiMode = ref('oficial');
const isEvolutionPreview = computed(() => previewApiMode.value === 'evolution');
const officialFormats = [
  'simple',
  'buttons',
  'quick_replies',
  'personalized',
  'carousel',
  'catalog',
  'coupon',
  'payment',
  'authentication',
  'flow',
  'list',
];
const evolutionFormats = [
  'simple',
  'buttons',
  'quick_replies',
  'personalized',
  'carousel',
  'catalog',
  'coupon',
  'authentication',
  'list',
];
const isFormatSupported = format => {
  if (!selectedInbox.value) return officialFormats.includes(format);
  if (isEvolutionInbox.value) return evolutionFormats.includes(format);
  return officialFormats.includes(format);
};
const selectFormat = format => {
  if (props.template?.id) return;
  if (!isFormatSupported(format)) {
    useAlert(
      format === 'list'
        ? t('ACTIVE_MESSAGES.LIST_UNSUPPORTED_INBOX')
        : t('ACTIVE_MESSAGES.FLOW_REQUIRES_OFFICIAL_INBOX')
    );
    return;
  }

  form.value.format = format;
  form.value.paymentDetails = format === 'payment';
  if (format === 'payment') {
    syncPaymentButtons();
  }
  if (format === 'authentication') {
    form.value.category = 'authentication';
    form.value.tokenCode ||= defaultTokenCode;
  } else if (form.value.category === 'authentication') {
    form.value.category = 'utility';
  }
};

watch(
  () => form.value.format,
  format => {
    isFlowPreviewOpen.value = false;
    if (format === 'flow') previewApiMode.value = 'oficial';
    if (format === 'payment') {
      form.value.paymentDetails = true;
    }
  }
);

watch(
  () => form.value.inbox_id,
  newInboxId => {
    const selected = inboxes.value.find(
      inbox => String(inbox.id) === String(newInboxId)
    );
    if (
      selected &&
      (selected.channel_type === 'Channel::Api' ||
        selected.channel_type === 'Channel::EvolutionWhatsapp')
    ) {
      previewApiMode.value = 'evolution';
      if (form.value.format === 'flow') {
        form.value.format = 'simple';
        useAlert(t('ACTIVE_MESSAGES.FLOW_REMOVED_EVOLUTION'));
      }
      if (form.value.format === 'payment') {
        form.value.format = 'simple';
        form.value.paymentDetails = false;
        useAlert(t('ACTIVE_MESSAGES.PAYMENT_UNAVAILABLE_EVOLUTION'));
      }
    } else {
      previewApiMode.value = 'oficial';
    }
  }
);

onMounted(() => {
  dialogRef.value?.open();
  if (teams.value.length === 0) {
    store.dispatch('teams/get');
  }

  if (props.template) {
    const v = props.template.variables || {};
    if (v.format) {
      form.value.format = v.format;
    } else if (v.useCarousel) form.value.format = 'carousel';
    else if (v.useList) form.value.format = 'list';
    else if (props.template.buttons?.length > 0) form.value.format = 'buttons';
    else if (props.template.category === 'authentication')
      form.value.format = 'authentication';
    else form.value.format = 'simple';

    form.value.couponCode =
      v.couponCode ||
      (form.value.format === 'coupon' && props.template.buttons?.length > 0
        ? props.template.buttons[0].url
        : '');
    form.value.tokenCode =
      v.tokenCode ||
      (form.value.format === 'authentication' &&
      props.template.buttons?.length > 0
        ? props.template.buttons[0].url
        : defaultTokenCode);
    form.value.catalogId =
      v.catalogId ||
      (form.value.format === 'catalog' && props.template.buttons?.length > 0
        ? props.template.buttons[0].url
        : '');
    form.value.codeExpirationMinutes = v.codeExpirationMinutes || 10;
    form.value.flowId = v.flowId || '';
    form.value.flowName = v.flowName || '';
    form.value.flowCta = v.flowCta || t('ACTIVE_MESSAGES.OPEN');
    form.value.flowScreen = v.flowScreen || '';
    form.value.flowToken = v.flowToken || '{{flow_token}}';
    form.value.flowJson = v.flowJson || null;
    form.value.flowJsonFileName = v.flowJsonFileName || '';
    form.value.listButtonText = v.listButtonText || '';
    form.value.listItems = Array.isArray(v.listItems) ? v.listItems : [];
    form.value.paymentDetails =
      v.format === 'payment' ||
      v.payment_details === true ||
      v.payment_details === 'true';
    form.value.paymentTitle =
      v.payment_title || t('ACTIVE_MESSAGES.PAYMENT_DEFAULT_TITLE');
    form.value.paymentProduct =
      v.payment_product || t('ACTIVE_MESSAGES.PAYMENT_DEFAULT_PRODUCT');
    form.value.paymentQuantity = v.payment_quantity || '1';
    form.value.paymentMethods = Array.isArray(v.payment_methods)
      ? v.payment_methods
      : ['pix'];
    form.value.paymentImageUrl = v.payment_image_url || '';
    form.value.paymentImageBlobId = v.payment_image_blob_id || '';
    if (form.value.format === 'payment') {
      form.value.paymentDetails = true;
      syncPaymentButtons();
    }
    flowJsonSource.value = form.value.flowJson
      ? JSON.stringify(form.value.flowJson, null, 2)
      : '';

    if (v.allowed_in_chat !== undefined) {
      form.value.allowed_in_chat =
        v.allowed_in_chat === true || v.allowed_in_chat === 'true';
    }
  }

  if (props.context === 'active_messages') {
    form.value.allowed_in_chat = true;
  }
});

const handleClose = () => {
  emit('close');
};

const isSaving = ref(false);

const handleSave = async () => {
  if (isSaving.value) return;
  isSaving.value = true;
  try {
    if (!form.value.name?.trim()) {
      useAlert(t('ACTIVE_MESSAGES.REQUIRED_TEMPLATE_NAME'));
      return;
    }
    if (!form.value.inbox_id) {
      useAlert(t('ACTIVE_MESSAGES.REQUIRED_INBOX'));
      return;
    }
    if (!form.value.content?.trim()) {
      useAlert(t('ACTIVE_MESSAGES.REQUIRED_MAIN_MESSAGE'));
      return;
    }
    if (!isFormatSupported(form.value.format)) {
      useAlert(t('ACTIVE_MESSAGES.UNSUPPORTED_FORMAT'));
      return;
    }
    if (
      isOfficialInbox.value &&
      form.value.useHeader &&
      !form.value.headerBlobId
    ) {
      useAlert(t('ACTIVE_MESSAGES.REQUIRED_HEADER_MEDIA'));
      return;
    }
    if (form.value.format === 'carousel') {
      const validCards = form.value.carouselItems.every(
        item =>
          (!isOfficialInbox.value || item.blobId) &&
          item.imageUrl &&
          item.url &&
          (item.title || item.description)
      );
      if (form.value.carouselItems.length < 2 || !validCards) {
        useAlert(
          isOfficialInbox.value
            ? t('ACTIVE_MESSAGES.OFFICIAL_CAROUSEL_INVALID')
            : t('ACTIVE_MESSAGES.CAROUSEL_INVALID')
        );
        return;
      }
      if (form.value.carouselItems.length > 10) {
        useAlert(t('ACTIVE_MESSAGES.CAROUSEL_MAX_CARDS'));
        return;
      }
    }
    if (
      ['buttons', 'quick_replies', 'personalized'].includes(form.value.format)
    ) {
      if (!form.value.buttons.length) {
        useAlert(t('ACTIVE_MESSAGES.REQUIRED_BUTTON'));
        return;
      }
      const invalidButton = form.value.buttons.find(button => {
        if (!button.label?.trim()) return true;
        if (button.label.trim().length > MAX_BUTTON_LABEL_LENGTH) return true;
        if (button.type === 'url') {
          return !/^https:\/\//i.test(button.url?.trim() || '');
        }
        if (button.type === 'phone') {
          return !/^\+\d{10,15}$/.test(button.url?.trim() || '');
        }
        if (button.payment_method) {
          if (
            !paymentActionOptions.some(
              item => item.key === button.payment_method
            )
          ) {
            return true;
          }
          if (!button.url?.trim()) return true;
          if (
            button.payment_method === 'payment_link' &&
            !/^https:\/\//i.test(button.url.trim())
          ) {
            return true;
          }
          if (
            button.payment_method === 'pix' &&
            (!button.merchant_name?.trim() || !button.pix_key?.trim())
          ) {
            return true;
          }
          return false;
        }
        return false;
      });
      const buttonErrors = validateTemplateButtons(
        form.value.format,
        form.value.buttons,
        t
      );
      if (invalidButton || buttonErrors.length) {
        useAlert(buttonErrors[0] || t('ACTIVE_MESSAGES.INVALID_BUTTONS'));
        return;
      }
    }
    if (form.value.format === 'list') {
      const buttonText = form.value.listButtonText?.trim() || '';
      const items = form.value.listItems || [];
      const invalidItem = items.find(
        item =>
          !item.title?.trim() ||
          item.title.trim().length > 24 ||
          (item.description || '').length > 72
      );
      if (
        buttonText.length < 1 ||
        buttonText.length > 20 ||
        items.length < 1 ||
        items.length > 10 ||
        invalidItem
      ) {
        useAlert(t('ACTIVE_MESSAGES.INVALID_LIST'));
        return;
      }
    }
    if (form.value.format === 'coupon') {
      const couponLength = form.value.couponCode?.trim().length || 0;
      if (couponLength < 1 || couponLength > 15) {
        useAlert(t('ACTIVE_MESSAGES.INVALID_COUPON'));
        return;
      }
    }
    if (form.value.format === 'authentication') {
      const expiration = Number(form.value.codeExpirationMinutes);
      if (!form.value.tokenCode?.trim()) {
        useAlert(t('ACTIVE_MESSAGES.REQUIRED_AUTH_CODE'));
        return;
      }
      if (!Number.isInteger(expiration) || expiration < 1 || expiration > 90) {
        useAlert(t('ACTIVE_MESSAGES.INVALID_AUTH_EXPIRATION'));
        return;
      }
    }
    if (
      form.value.format === 'flow' &&
      !(
        form.value.flowId?.trim() ||
        form.value.flowName?.trim() ||
        form.value.flowJson
      )
    ) {
      useAlert(t('ACTIVE_MESSAGES.REQUIRED_FLOW'));
      return;
    }
    if (form.value.format === 'flow' && flowJsonSource.value.trim()) {
      try {
        form.value.flowJson = parseFlowJson(flowJsonSource.value, t);
      } catch (error) {
        flowJsonError.value = error.message;
        useAlert(error.message);
        return;
      }
    }
    if (
      form.value.format === 'payment' &&
      form.value.paymentImageUrl.trim() &&
      !/^https:\/\//i.test(form.value.paymentImageUrl.trim())
    ) {
      useAlert(t('ACTIVE_MESSAGES.INVALID_PAYMENT_IMAGE'));
      return;
    }
    if (form.value.format === 'payment') {
      const quantity = Number(form.value.paymentQuantity);
      const paymentButtons = form.value.buttons.filter(
        button => button.type === 'payment_method'
      );
      if (!form.value.paymentMethods.length) {
        useAlert(t('ACTIVE_MESSAGES.REQUIRED_PAYMENT_METHOD'));
        return;
      }
      if (paymentButtons.length !== 1) {
        useAlert(t('ACTIVE_MESSAGES.REQUIRED_PAYMENT_ACTION'));
        return;
      }
      const paymentButton = paymentButtons[0];
      if (
        !paymentActionOptions.some(
          option => option.key === paymentButton.payment_method
        ) ||
        !paymentButton.url?.trim() ||
        (paymentButton.payment_method === 'payment_link' &&
          !/^https:\/\//i.test(paymentButton.url.trim())) ||
        (paymentButton.payment_method === 'pix' &&
          (!paymentButton.merchant_name?.trim() ||
            !paymentButton.pix_key?.trim()))
      ) {
        useAlert(t('ACTIVE_MESSAGES.INVALID_PAYMENT_ACTION'));
        return;
      }
      if (!Number.isInteger(quantity) || quantity < 1) {
        useAlert(t('ACTIVE_MESSAGES.INVALID_PAYMENT_QUANTITY'));
        return;
      }
    }
    if (
      form.value.format === 'flow' &&
      (form.value.flowCta?.trim().length < 1 ||
        form.value.flowCta.trim().length > 20)
    ) {
      useAlert(t('ACTIVE_MESSAGES.INVALID_FLOW_BUTTON'));
      return;
    }

    let finalButtons = [];
    if (
      ['buttons', 'quick_replies', 'personalized'].includes(form.value.format)
    ) {
      finalButtons = form.value.buttons;
    } else if (form.value.format === 'authentication') {
      finalButtons = [
        {
          type: 'copy_code',
          label: t('ACTIVE_MESSAGES.COPY_CODE'),
          url: normalizeVariableSyntax(form.value.tokenCode),
        },
      ];
    } else if (form.value.format === 'coupon') {
      finalButtons = [
        {
          type: 'copy_code',
          label: t('ACTIVE_MESSAGES.COPY_COUPON'),
          url: form.value.couponCode,
        },
      ];
    } else if (form.value.format === 'catalog') {
      finalButtons = [
        {
          type: 'catalog',
          label: t('ACTIVE_MESSAGES.VIEW_CATALOG'),
          url: form.value.catalogId,
        },
      ];
    } else if (form.value.format === 'payment') {
      finalButtons = form.value.buttons.filter(
        button => button.type === 'payment_method'
      );
    }

    const payload = {
      name: form.value.name,
      category:
        form.value.format === 'authentication'
          ? 'authentication'
          : form.value.category,
      content: normalizeVariableSyntax(form.value.content),
      inbox_id: form.value.inbox_id,
      team_id: form.value.team_id,
      buttons: finalButtons,
      variables: {
        format: form.value.format,
        footer: normalizeVariableSyntax(form.value.footer),
        useCarousel: form.value.format === 'carousel',
        carouselItems:
          form.value.format === 'carousel' ? form.value.carouselItems : [],
        useWebhook: form.value.useWebhook,
        webhookUrl: form.value.webhookUrl,
        useHeader: [
          'simple',
          'buttons',
          'quick_replies',
          'personalized',
          'coupon',
          'payment',
          'catalog',
          'flow',
        ].includes(form.value.format)
          ? form.value.useHeader
          : false,
        headerType: form.value.headerType,
        headerUrl: form.value.headerUrl,
        headerBlobId: form.value.headerBlobId,
        allowed_in_chat: form.value.allowed_in_chat,
        couponCode: form.value.format === 'coupon' ? form.value.couponCode : '',
        tokenCode:
          form.value.format === 'authentication'
            ? normalizeVariableSyntax(form.value.tokenCode)
            : '',
        codeExpirationMinutes:
          form.value.format === 'authentication'
            ? Number(form.value.codeExpirationMinutes)
            : 10,
        catalogId: form.value.format === 'catalog' ? form.value.catalogId : '',
        flowId: form.value.format === 'flow' ? form.value.flowId : '',
        flowName: form.value.format === 'flow' ? form.value.flowName : '',
        flowCta: form.value.format === 'flow' ? form.value.flowCta : '',
        flowScreen: form.value.format === 'flow' ? form.value.flowScreen : '',
        flowToken: form.value.format === 'flow' ? form.value.flowToken : '',
        flowJson: form.value.format === 'flow' ? form.value.flowJson : null,
        flowJsonFileName:
          form.value.format === 'flow' ? form.value.flowJsonFileName : '',
        useList: form.value.format === 'list',
        listButtonText:
          form.value.format === 'list' ? form.value.listButtonText : '',
        listItems: form.value.format === 'list' ? form.value.listItems : [],
        payment_details: form.value.format === 'payment',
        payment_title:
          form.value.format === 'payment' ? form.value.paymentTitle : '',
        payment_product:
          form.value.format === 'payment' ? form.value.paymentProduct : '',
        payment_quantity:
          form.value.format === 'payment' ? form.value.paymentQuantity : '1',
        payment_methods:
          form.value.format === 'payment' ? form.value.paymentMethods : [],
        payment_image_url:
          form.value.format === 'payment' ? form.value.paymentImageUrl : '',
        payment_image_blob_id:
          form.value.format === 'payment' ? form.value.paymentImageBlobId : '',
        is_exclusive_active_message:
          props.template?.variables?.is_exclusive_active_message === true ||
          props.template?.variables?.is_exclusive_active_message === 'true',
      },
    };

    if (props.context === 'active_messages') {
      payload.variables.allowed_in_chat = true;
      if (!props.template?.id) {
        payload.variables.is_exclusive_active_message = true;
      }
    }

    if (props.template?.id) {
      await store.dispatch('customMessageTemplates/update', {
        id: props.template.id,
        ...payload,
      });
      useAlert(t('CAMPAIGN.SMS.ALERTS.TEMPLATE_UPDATED'));
    } else {
      await store.dispatch('customMessageTemplates/create', payload);
      useAlert(t('CAMPAIGN.SMS.ALERTS.TEMPLATE_CREATED'));
    }
    handleClose();
  } catch (error) {
    const errorMsg =
      error?.response?.data?.error ||
      error?.message ||
      t('CAMPAIGN.SMS.ALERTS.TEMPLATE_SAVE_ERROR');
    useAlert(errorMsg);
  } finally {
    isSaving.value = false;
  }
};

const addCarouselItem = () => {
  form.value.carouselItems.push({
    title: '',
    description: '',
    imageUrl: '',
    blobId: null,
    url: '',
    buttonText: 'Ver mais',
  });
};

const addListItem = () => {
  if (form.value.listItems.length < 10) {
    form.value.listItems.push({ title: '', description: '' });
  }
};

const addButton = () => {
  if (form.value.buttons.length < MAX_TEMPLATE_BUTTONS) {
    // A mídia/anexo é independente do formato de ação. Ao adicionar o
    // primeiro botão em um modelo simples, convertemos o modelo para o
    // formato oficial de botões e preservamos o cabeçalho, se houver.
    if (form.value.format === 'simple') form.value.format = 'buttons';
    form.value.buttons.push({ label: '', type: 'reply', actionType: 'reply' });
  }
};

const formatPhoneLocal = (button, event) => {
  const digits = String(event?.target?.value || '').replace(/\D/g, '');
  button.phoneLocal = digits;
  button.url = `${button.countryCode || '+55'}${digits}`;
};

const formatPhoneLocalValue = button => {
  const digits = String(button.phoneLocal || '').replace(/\D/g, '');
  if (button.countryCode !== '+55' || digits.length < 3) return digits;

  const areaCode = digits.slice(0, 2);
  const subscriber = digits.slice(2);
  const separator = subscriber.length > 4 ? '-' : '';
  const splitAt = subscriber.length > 8 ? 5 : 4;
  const formattedSubscriber = separator
    ? `${subscriber.slice(0, splitAt)}${separator}${subscriber.slice(splitAt)}`
    : subscriber;
  return `(${areaCode}) ${formattedSubscriber}`;
};

const handleButtonTypeChange = (button, event) => {
  const selectedType = event?.target?.value || button.actionType;
  button.actionType = selectedType;

  if (selectedType === 'payment' || selectedType?.startsWith('payment:')) {
    const method = selectedType.startsWith('payment:')
      ? selectedType.replace('payment:', '')
      : button.payment_method || 'pix';
    button.payment_method = method;
    button.type = 'payment_method';
    button.label = t('ACTIVE_MESSAGES.REQUEST_PAYMENT');
    return;
  }

  delete button.payment_method;
  button.type = selectedType;
  if (button.type === 'phone') {
    Object.assign(button, extractPhoneParts(button.url || '+55'));
    button.url = `${button.countryCode}${button.phoneLocal}`;
  }
};

const canSelectButtonType = (type, index) =>
  canAddButtonType(form.value.buttons, type, index);

const buttonTypeLimit = type =>
  BUTTON_TYPE_LIMITS[type] || MAX_TEMPLATE_BUTTONS;
</script>

<template>
  <!-- eslint-disable @intlify/vue-i18n/no-raw-text, vue/html-closing-bracket-newline, vue/html-quotes -->
  <!-- eslint-disable vue/no-bare-strings-in-template, vue/no-static-inline-styles, vue/html-self-closing -->
  <Dialog
    ref="dialogRef"
    width="5xl"
    class="!max-w-[1280px]"
    :show-confirm-button="false"
    :show-cancel-button="false"
    @close="handleClose"
  >
    <div
      class="flex h-[calc(100dvh-7rem)] min-h-0 flex-col overflow-hidden rounded-xl bg-white dark:bg-slate-800 sm:h-[80vh] xl:flex-row -m-6"
    >
      <!-- Editor Column -->
      <div
        class="min-h-0 flex-1 overflow-y-auto border-b border-slate-100 p-4 sm:p-6 xl:h-auto xl:border-b-0 xl:border-r"
      >
        <h2
          class="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-6 bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent"
        >
          <template v-if="context === 'active_messages'">
            {{
              template?.id
                ? t('ACTIVE_MESSAGES.EDIT_TITLE')
                : t('ACTIVE_MESSAGES.TITLE')
            }}
          </template>
          <template v-else>
            {{
              template?.id
                ? t('ACTIVE_MESSAGES.EDIT_PREMIUM')
                : t('ACTIVE_MESSAGES.NEW_PREMIUM')
            }}
          </template>
        </h2>

        <div class="space-y-6">
          <div
            class="grid grid-cols-1 items-start gap-4 sm:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]"
          >
            <Input
              v-model="form.name"
              :label="t('ACTIVE_MESSAGES.MODEL_NAME')"
              :placeholder="t('ACTIVE_MESSAGES.MODEL_NAME_PLACEHOLDER')"
            />
            <div class="relative flex flex-col min-w-0 gap-1">
              <label
                for="active-message-category"
                class="mb-0.5 whitespace-nowrap text-heading-3 text-n-slate-12"
                >{{ t('ACTIVE_MESSAGES.CATEGORY') }}</label
              >
              <div class="relative w-full">
                <select
                  id="active-message-category"
                  v-model="form.category"
                  :disabled="form.format === 'authentication'"
                  class="appearance-none bg-none block w-full reset-base text-sm !mb-0 outline outline-1 border-none border-0 outline-offset-[-1px] rounded-lg bg-n-alpha-black2 py-[9px] pl-3 pr-10 outline-n-weak focus:outline-n-brand text-n-slate-12 transition-all duration-500 ease-in-out cursor-pointer"
                >
                  <option value="marketing">
                    {{ t('ACTIVE_MESSAGES.CATEGORY_MARKETING') }}
                  </option>
                  <option value="utility">
                    {{ t('ACTIVE_MESSAGES.CATEGORY_UTILITY') }}
                  </option>
                  <option value="authentication">
                    {{ t('ACTIVE_MESSAGES.CATEGORY_AUTHENTICATION') }}
                  </option>
                </select>
                <div
                  class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none"
                >
                  <div class="i-lucide-chevron-down size-4 text-n-slate-11" />
                </div>
              </div>
            </div>

            <div class="relative flex flex-col min-w-0 gap-1">
              <label
                for="active-message-inbox"
                class="mb-0.5 whitespace-nowrap text-heading-3 text-n-slate-12"
                >{{ t('ACTIVE_MESSAGES.INBOX_LABEL') }}</label
              >
              <div class="relative w-full">
                <select
                  id="active-message-inbox"
                  v-model="form.inbox_id"
                  class="appearance-none bg-none block w-full reset-base text-sm !mb-0 outline outline-1 border-none border-0 outline-offset-[-1px] rounded-lg bg-n-alpha-black2 py-[9px] pl-3 pr-10 outline-n-weak focus:outline-n-brand text-n-slate-12 transition-all duration-500 ease-in-out cursor-pointer"
                >
                  <option :value="null" disabled>
                    {{ t('ACTIVE_MESSAGES.SELECT_INBOX') }}
                  </option>
                  <option
                    v-for="inbox in inboxes"
                    :key="inbox.id"
                    :value="inbox.id"
                  >
                    {{ inbox.name }}
                  </option>
                </select>
                <div
                  class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none"
                >
                  <div class="i-lucide-chevron-down size-4 text-n-slate-11" />
                </div>
              </div>
            </div>

            <div
              v-if="selectedInbox"
              class="order-last flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900/50 sm:col-span-2"
            >
              <div
                class="i-lucide-send-horizontal mt-0.5 shrink-0 text-lg text-n-slate-11"
                aria-hidden="true"
              />
              <div class="min-w-0 flex-1">
                <div class="flex flex-wrap items-center gap-2">
                  <span class="text-sm font-semibold text-n-slate-12">
                    Direct Send
                  </span>
                  <span
                    class="rounded-full px-2 py-0.5 text-xs font-semibold"
                    :class="directSendStatusClass"
                  >
                    {{ directSendStatusLabel }}
                  </span>
                </div>
                <p class="mt-1 text-xs text-n-slate-11">
                  <template v-if="directSendStatus === 'available'">
                    {{ t('ACTIVE_MESSAGES.DIRECT_SEND_AVAILABLE') }}
                  </template>
                  <template v-else-if="directSendStatus === 'not_applicable'">
                    {{ t('ACTIVE_MESSAGES.DIRECT_SEND_EVOLUTION') }}
                  </template>
                  <template v-else>
                    {{ t('ACTIVE_MESSAGES.DIRECT_SEND_PENDING') }}
                  </template>
                </p>
              </div>
            </div>

            <div class="relative flex flex-col min-w-0 gap-1">
              <label
                for="active-message-team"
                class="mb-0.5 whitespace-nowrap text-heading-3 text-n-slate-12"
                >{{ t('ACTIVE_MESSAGES.TEAM_LABEL') }}</label
              >
              <div class="relative w-full">
                <select
                  id="active-message-team"
                  v-model="form.team_id"
                  class="appearance-none bg-none block w-full reset-base text-sm !mb-0 outline outline-1 border-none border-0 outline-offset-[-1px] rounded-lg bg-n-alpha-black2 py-[9px] pl-3 pr-10 outline-n-weak focus:outline-n-brand text-n-slate-12 transition-all duration-500 ease-in-out cursor-pointer"
                >
                  <option :value="null">
                    {{ t('ACTIVE_MESSAGES.ALL_TEAMS') }}
                  </option>
                  <option v-for="team in teams" :key="team.id" :value="team.id">
                    {{ team.name }}
                  </option>
                </select>
                <div
                  class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none"
                >
                  <div class="i-lucide-chevron-down size-4 text-n-slate-11" />
                </div>
              </div>
            </div>
          </div>

          <!-- Permissões de Uso -->
          <div
            v-if="context !== 'active_messages'"
            class="p-4 border border-slate-200 dark:border-slate-700 rounded-xl bg-indigo-50/30"
          >
            <div class="flex items-center justify-between">
              <div>
                <h4
                  class="text-sm font-bold text-slate-900 dark:text-slate-100 mb-1"
                >
                  {{ t('ACTIVE_MESSAGES.AVAILABLE_IN_CHAT') }}
                </h4>
                <p class="text-xs text-slate-500 dark:text-slate-400">
                  {{ t('ACTIVE_MESSAGES.AVAILABLE_IN_CHAT_DESCRIPTION') }}
                </p>
              </div>
              <Switch v-model="form.allowed_in_chat" />
            </div>
          </div>

          <!-- Seleção de Formato (Botões Grandes / Cards) -->
          <div class="mt-4">
            <label
              id="active-message-format-label"
              class="mb-2 block text-heading-3 text-n-slate-12"
              >{{ t('ACTIVE_MESSAGES.FORMAT_LABEL') }}</label
            >
            <div
              class="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3"
              role="radiogroup"
              aria-labelledby="active-message-format-label"
            >
              <div
                role="radio"
                :aria-checked="form.format === 'simple'"
                :aria-disabled="Boolean(props.template?.id)"
                :tabindex="props.template?.id ? -1 : 0"
                class="flex flex-col items-center justify-center p-4 border rounded-xl transition-all"
                :class="[
                  form.format === 'simple'
                    ? 'border-indigo-500 bg-indigo-50/30 dark:bg-indigo-500/10 shadow-sm'
                    : 'border-slate-200 dark:border-slate-700',
                  props.template?.id
                    ? 'opacity-60 cursor-not-allowed'
                    : 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800',
                ]"
                @click="selectFormat('simple')"
                @keydown.enter.prevent="selectFormat('simple')"
                @keydown.space.prevent="selectFormat('simple')"
              >
                <div
                  class="i-lucide-message-square text-2xl mb-2"
                  :class="
                    form.format === 'simple'
                      ? 'text-indigo-600 dark:text-indigo-400'
                      : 'text-slate-400'
                  "
                />
                <span
                  class="text-sm font-semibold text-center"
                  :class="
                    form.format === 'simple'
                      ? 'text-indigo-700 dark:text-indigo-300'
                      : 'text-slate-600 dark:text-slate-300'
                  "
                  >{{ t('ACTIVE_MESSAGES.FORMAT_SIMPLE') }}</span
                >
              </div>
              <div
                role="radio"
                :aria-checked="form.format === 'payment'"
                :aria-disabled="
                  Boolean(props.template?.id) || !isFormatSupported('payment')
                "
                :tabindex="
                  props.template?.id || !isFormatSupported('payment') ? -1 : 0
                "
                class="flex flex-col items-center justify-center p-4 border rounded-xl transition-all"
                :class="[
                  form.format === 'payment'
                    ? 'border-indigo-500 bg-indigo-50/30 dark:bg-indigo-500/10 shadow-sm'
                    : 'border-slate-200 dark:border-slate-700',
                  props.template?.id || !isFormatSupported('payment')
                    ? 'opacity-60 cursor-not-allowed'
                    : 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800',
                ]"
                @click="selectFormat('payment')"
                @keydown.enter.prevent="selectFormat('payment')"
                @keydown.space.prevent="selectFormat('payment')"
              >
                <div
                  class="i-lucide-wallet-cards text-2xl mb-2"
                  :class="
                    form.format === 'payment'
                      ? 'text-indigo-600 dark:text-indigo-400'
                      : 'text-slate-400'
                  "
                />
                <span
                  class="text-sm font-semibold text-center"
                  :class="
                    form.format === 'payment'
                      ? 'text-indigo-700 dark:text-indigo-300'
                      : 'text-slate-600 dark:text-slate-300'
                  "
                  >{{ t('ACTIVE_MESSAGES.FORMAT_PAYMENT') }}</span
                >
              </div>
              <div
                role="radio"
                :aria-checked="form.format === 'buttons'"
                :aria-disabled="Boolean(props.template?.id)"
                :tabindex="props.template?.id ? -1 : 0"
                class="flex flex-col items-center justify-center p-4 border rounded-xl transition-all"
                :class="[
                  form.format === 'buttons'
                    ? 'border-indigo-500 bg-indigo-50/30 dark:bg-indigo-500/10 shadow-sm'
                    : 'border-slate-200 dark:border-slate-700',
                  props.template?.id
                    ? 'opacity-60 cursor-not-allowed'
                    : 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800',
                ]"
                @click="selectFormat('buttons')"
                @keydown.enter.prevent="selectFormat('buttons')"
                @keydown.space.prevent="selectFormat('buttons')"
              >
                <div
                  class="i-lucide-mouse-pointer-click text-2xl mb-2"
                  :class="
                    form.format === 'buttons'
                      ? 'text-indigo-600 dark:text-indigo-400'
                      : 'text-slate-400'
                  "
                />
                <span
                  class="text-sm font-semibold text-center"
                  :class="
                    form.format === 'buttons'
                      ? 'text-indigo-700 dark:text-indigo-300'
                      : 'text-slate-600 dark:text-slate-300'
                  "
                  >{{ t('ACTIVE_MESSAGES.FORMAT_BUTTONS') }}</span
                >
              </div>
              <div
                role="radio"
                :aria-checked="form.format === 'quick_replies'"
                :aria-disabled="Boolean(props.template?.id)"
                :tabindex="props.template?.id ? -1 : 0"
                class="flex flex-col items-center justify-center p-4 border rounded-xl transition-all"
                :class="[
                  form.format === 'quick_replies'
                    ? 'border-indigo-500 bg-indigo-50/30 dark:bg-indigo-500/10 shadow-sm'
                    : 'border-slate-200 dark:border-slate-700',
                  props.template?.id
                    ? 'opacity-60 cursor-not-allowed'
                    : 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800',
                ]"
                @click="selectFormat('quick_replies')"
                @keydown.enter.prevent="selectFormat('quick_replies')"
                @keydown.space.prevent="selectFormat('quick_replies')"
              >
                <div
                  class="i-lucide-message-circle-more text-2xl mb-2"
                  :class="
                    form.format === 'quick_replies'
                      ? 'text-indigo-600 dark:text-indigo-400'
                      : 'text-slate-400'
                  "
                />
                <span
                  class="text-sm font-semibold text-center"
                  :class="
                    form.format === 'quick_replies'
                      ? 'text-indigo-700 dark:text-indigo-300'
                      : 'text-slate-600 dark:text-slate-300'
                  "
                  >{{ t('ACTIVE_MESSAGES.FORMAT_QUICK_REPLIES') }}</span
                >
              </div>
              <div
                role="radio"
                :aria-checked="form.format === 'list'"
                :aria-disabled="
                  Boolean(props.template?.id) || !isFormatSupported('list')
                "
                :tabindex="
                  props.template?.id || !isFormatSupported('list') ? -1 : 0
                "
                class="flex flex-col items-center justify-center p-4 border rounded-xl transition-all"
                :class="[
                  form.format === 'list'
                    ? 'border-indigo-500 bg-indigo-50/30 dark:bg-indigo-500/10 shadow-sm'
                    : 'border-slate-200 dark:border-slate-700',
                  props.template?.id || !isFormatSupported('list')
                    ? 'opacity-60 cursor-not-allowed'
                    : 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800',
                ]"
                @click="selectFormat('list')"
                @keydown.enter.prevent="selectFormat('list')"
                @keydown.space.prevent="selectFormat('list')"
              >
                <div
                  class="i-lucide-list text-2xl mb-2"
                  :class="
                    form.format === 'list'
                      ? 'text-indigo-600 dark:text-indigo-400'
                      : 'text-slate-400'
                  "
                />
                <span
                  class="text-sm font-semibold text-center"
                  :class="
                    form.format === 'list'
                      ? 'text-indigo-700 dark:text-indigo-300'
                      : 'text-slate-600 dark:text-slate-300'
                  "
                  >{{ t('ACTIVE_MESSAGES.FORMAT_LIST') }}</span
                >
              </div>
              <div
                role="radio"
                :aria-checked="form.format === 'carousel'"
                :tabindex="props.template?.id ? -1 : 0"
                class="flex flex-col items-center justify-center p-4 border rounded-xl transition-all"
                :class="[
                  form.format === 'carousel'
                    ? 'border-indigo-500 bg-indigo-50/30 dark:bg-indigo-500/10 shadow-sm'
                    : 'border-slate-200 dark:border-slate-700',
                  props.template?.id
                    ? 'opacity-60 cursor-not-allowed'
                    : 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800',
                ]"
                :aria-disabled="!isFormatSupported('carousel')"
                @click="selectFormat('carousel')"
                @keydown.enter.prevent="selectFormat('carousel')"
                @keydown.space.prevent="selectFormat('carousel')"
              >
                <div
                  class="i-lucide-gallery-horizontal-end text-2xl mb-2"
                  :class="
                    form.format === 'carousel'
                      ? 'text-indigo-600 dark:text-indigo-400'
                      : 'text-slate-400'
                  "
                />
                <span
                  class="text-sm font-semibold text-center"
                  :class="
                    form.format === 'carousel'
                      ? 'text-indigo-700 dark:text-indigo-300'
                      : 'text-slate-600 dark:text-slate-300'
                  "
                  >{{ t('ACTIVE_MESSAGES.FORMAT_CAROUSEL') }}</span
                >
              </div>
              <div
                role="radio"
                :aria-checked="form.format === 'catalog'"
                :tabindex="props.template?.id ? -1 : 0"
                class="flex flex-col items-center justify-center p-4 border rounded-xl transition-all"
                :class="[
                  form.format === 'catalog'
                    ? 'border-indigo-500 bg-indigo-50/30 dark:bg-indigo-500/10 shadow-sm'
                    : 'border-slate-200 dark:border-slate-700',
                  props.template?.id
                    ? 'opacity-60 cursor-not-allowed'
                    : 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800',
                ]"
                :aria-disabled="!isFormatSupported('catalog')"
                @click="selectFormat('catalog')"
                @keydown.enter.prevent="selectFormat('catalog')"
                @keydown.space.prevent="selectFormat('catalog')"
              >
                <div
                  class="i-lucide-store text-2xl mb-2"
                  :class="
                    form.format === 'catalog'
                      ? 'text-indigo-600 dark:text-indigo-400'
                      : 'text-slate-400'
                  "
                />
                <span
                  class="text-sm font-semibold text-center"
                  :class="
                    form.format === 'catalog'
                      ? 'text-indigo-700 dark:text-indigo-300'
                      : 'text-slate-600 dark:text-slate-300'
                  "
                  >{{ t('ACTIVE_MESSAGES.FORMAT_CATALOG') }}</span
                >
              </div>
              <div
                role="radio"
                :aria-checked="form.format === 'coupon'"
                :tabindex="props.template?.id ? -1 : 0"
                class="flex flex-col items-center justify-center p-4 border rounded-xl transition-all"
                :class="[
                  form.format === 'coupon'
                    ? 'border-indigo-500 bg-indigo-50/30 dark:bg-indigo-500/10 shadow-sm'
                    : 'border-slate-200 dark:border-slate-700',
                  props.template?.id
                    ? 'opacity-60 cursor-not-allowed'
                    : 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800',
                ]"
                :aria-disabled="!isFormatSupported('coupon')"
                @click="selectFormat('coupon')"
                @keydown.enter.prevent="selectFormat('coupon')"
                @keydown.space.prevent="selectFormat('coupon')"
              >
                <div
                  class="i-lucide-ticket text-2xl mb-2"
                  :class="
                    form.format === 'coupon'
                      ? 'text-indigo-600 dark:text-indigo-400'
                      : 'text-slate-400'
                  "
                />
                <span
                  class="text-sm font-semibold text-center"
                  :class="
                    form.format === 'coupon'
                      ? 'text-indigo-700 dark:text-indigo-300'
                      : 'text-slate-600 dark:text-slate-300'
                  "
                  >{{ t('ACTIVE_MESSAGES.FORMAT_COUPON') }}</span
                >
              </div>
              <div
                role="radio"
                :aria-checked="form.format === 'authentication'"
                :tabindex="props.template?.id ? -1 : 0"
                class="flex flex-col items-center justify-center p-4 border rounded-xl transition-all"
                :class="[
                  form.format === 'authentication'
                    ? 'border-indigo-500 bg-indigo-50/30 dark:bg-indigo-500/10 shadow-sm'
                    : 'border-slate-200 dark:border-slate-700',
                  props.template?.id
                    ? 'opacity-60 cursor-not-allowed'
                    : 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800',
                ]"
                :aria-disabled="!isFormatSupported('authentication')"
                @click="selectFormat('authentication')"
                @keydown.enter.prevent="selectFormat('authentication')"
                @keydown.space.prevent="selectFormat('authentication')"
              >
                <div
                  class="i-lucide-shield-check text-2xl mb-2"
                  :class="
                    form.format === 'authentication'
                      ? 'text-indigo-600 dark:text-indigo-400'
                      : 'text-slate-400'
                  "
                />
                <span
                  class="text-sm font-semibold text-center"
                  :class="
                    form.format === 'authentication'
                      ? 'text-indigo-700 dark:text-indigo-300'
                      : 'text-slate-600 dark:text-slate-300'
                  "
                  >{{ t('ACTIVE_MESSAGES.FORMAT_AUTHENTICATION') }}</span
                >
              </div>
              <div
                role="radio"
                :aria-checked="form.format === 'flow'"
                :tabindex="props.template?.id ? -1 : 0"
                class="flex flex-col items-center justify-center p-4 border rounded-xl transition-all"
                :class="[
                  form.format === 'flow'
                    ? 'border-indigo-500 bg-indigo-50/30 dark:bg-indigo-500/10 shadow-sm'
                    : 'border-slate-200 dark:border-slate-700',
                  props.template?.id || !isFormatSupported('flow')
                    ? 'opacity-60 cursor-not-allowed'
                    : 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800',
                ]"
                :aria-disabled="
                  Boolean(props.template?.id) || !isFormatSupported('flow')
                "
                @click="selectFormat('flow')"
                @keydown.enter.prevent="selectFormat('flow')"
                @keydown.space.prevent="selectFormat('flow')"
              >
                <div
                  class="i-lucide-workflow text-2xl mb-2"
                  :class="
                    form.format === 'flow'
                      ? 'text-indigo-600 dark:text-indigo-400'
                      : 'text-slate-400'
                  "
                />
                <span class="text-sm font-semibold text-center">
                  {{ t('ACTIVE_MESSAGES.FORMAT_FLOW') }}
                </span>
              </div>
              <div
                role="radio"
                :aria-checked="form.format === 'personalized'"
                :aria-disabled="Boolean(props.template?.id)"
                :tabindex="props.template?.id ? -1 : 0"
                class="flex flex-col items-center justify-center p-4 border rounded-xl transition-all"
                :class="[
                  form.format === 'personalized'
                    ? 'border-indigo-500 bg-indigo-50/30 dark:bg-indigo-500/10 shadow-sm'
                    : 'border-slate-200 dark:border-slate-700',
                  props.template?.id
                    ? 'opacity-60 cursor-not-allowed'
                    : 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800',
                ]"
                @click="selectFormat('personalized')"
                @keydown.enter.prevent="selectFormat('personalized')"
                @keydown.space.prevent="selectFormat('personalized')"
              >
                <div
                  class="i-lucide-panels-top-left text-2xl mb-2"
                  :class="
                    form.format === 'personalized'
                      ? 'text-indigo-600 dark:text-indigo-400'
                      : 'text-slate-400'
                  "
                />
                <span
                  class="text-sm font-semibold text-center"
                  :class="
                    form.format === 'personalized'
                      ? 'text-indigo-700 dark:text-indigo-300'
                      : 'text-slate-600 dark:text-slate-300'
                  "
                >
                  {{ t('ACTIVE_MESSAGES.FORMAT_CUSTOM') }}
                </span>
              </div>
            </div>
            <p
              v-if="isOfficialInbox || !selectedInbox"
              class="mt-3 text-xs text-slate-500 dark:text-slate-400"
            >
              {{ t('ACTIVE_MESSAGES.OFFICIAL_FORMATS_DESCRIPTION') }}
            </p>
            <p
              v-if="form.format === 'list'"
              class="mt-3 text-xs text-slate-500 dark:text-slate-400"
            >
              {{ t('ACTIVE_MESSAGES.LIST_FORMAT_DESCRIPTION') }}
            </p>
          </div>

          <!-- Detalhes de pagamento -->
          <div
            class="p-4 border border-indigo-200 dark:border-indigo-900 rounded-xl bg-indigo-50/40 dark:bg-indigo-950/20 relative mt-4"
          >
            <div class="flex justify-between items-center gap-3 mb-2">
              <div>
                <h3
                  class="font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-2"
                >
                  <div class="i-lucide-wallet-cards text-indigo-500" />
                  {{ t('ACTIVE_MESSAGES.PAYMENT_DETAILS') }}
                </h3>
                <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  {{ t('ACTIVE_MESSAGES.PAYMENT_DETAILS_DESCRIPTION') }}
                </p>
              </div>
              <Switch
                :model-value="form.paymentDetails"
                :aria-label="t('ACTIVE_MESSAGES.USE_PAYMENT_DETAILS')"
                @update:model-value="togglePaymentDetails"
              />
            </div>
            <div
              v-if="form.format === 'payment'"
              class="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3"
            >
              <Input
                v-model="form.paymentTitle"
                :label="t('ACTIVE_MESSAGES.PAYMENT_TITLE')"
                :placeholder="t('ACTIVE_MESSAGES.PAYMENT_TITLE_PLACEHOLDER')"
              />
              <Input
                v-model="form.paymentProduct"
                :label="t('ACTIVE_MESSAGES.PAYMENT_PRODUCT')"
                :placeholder="t('ACTIVE_MESSAGES.PAYMENT_PRODUCT_PLACEHOLDER')"
              />
              <Input
                v-model="form.paymentQuantity"
                :label="t('ACTIVE_MESSAGES.PAYMENT_QUANTITY')"
                placeholder="1"
              />
              <div class="flex items-end gap-2">
                <Input
                  v-model="form.paymentImageUrl"
                  class="min-w-0 flex-1"
                  :label="t('ACTIVE_MESSAGES.PAYMENT_IMAGE')"
                  :placeholder="t('ACTIVE_MESSAGES.PAYMENT_IMAGE_PLACEHOLDER')"
                />
                <Button
                  variant="outline"
                  class="mb-0.5 shrink-0"
                  :title="t('ACTIVE_MESSAGES.UPLOAD_ITEM_IMAGE')"
                  :aria-label="t('ACTIVE_MESSAGES.UPLOAD_ITEM_IMAGE')"
                  @click="triggerPaymentImageUpload"
                >
                  <div class="i-lucide-upload" />
                </Button>
                <input
                  ref="paymentImageUploadInput"
                  type="file"
                  class="hidden"
                  accept="image/jpeg,image/png,image/webp"
                  @change="handlePaymentImageUpload"
                />
              </div>
              <p
                class="md:col-span-2 -mt-1 text-xs text-slate-500 dark:text-slate-400"
              >
                {{ t('ACTIVE_MESSAGES.PAYMENT_IMAGE_DESCRIPTION') }}
              </p>
              <div class="md:col-span-2">
                <p
                  class="text-sm font-medium text-slate-700 dark:text-slate-200 mb-2"
                >
                  {{ t('ACTIVE_MESSAGES.DISPLAYED_PAYMENT_METHODS') }}
                </p>
                <div class="flex flex-wrap gap-2">
                  <button
                    v-for="method in paymentMethodOptions"
                    :key="method.key"
                    type="button"
                    class="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors"
                    :class="
                      form.paymentMethods.includes(method.key)
                        ? 'border-indigo-500 bg-indigo-500/10 text-indigo-700 dark:text-indigo-300'
                        : 'border-slate-300 dark:border-slate-700 text-slate-500'
                    "
                    @click="togglePaymentMethod(method.key)"
                  >
                    <div :class="method.icon" />
                    {{ method.label }}
                  </button>
                </div>
                <p class="mt-2 text-xs text-slate-500 dark:text-slate-400">
                  {{ t('ACTIVE_MESSAGES.PAYMENT_VARIABLES_DESCRIPTION') }}
                  <code v-pre>{{ valor }}</code>
                  {{ t('ACTIVE_MESSAGES.PAYMENT_VARIABLES_DESCRIPTION_TAIL') }}
                </p>
              </div>
            </div>
          </div>

          <!-- Anexo (Cabeçalho) -->
          <template
            v-if="
              form.format !== 'carousel' && form.format !== 'authentication'
            "
          >
            <div
              class="p-4 border border-slate-200 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-900/50 relative mt-4"
            >
              <div class="flex justify-between items-center mb-3">
                <h3
                  class="font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-2"
                >
                  <div class="i-lucide-paperclip" />
                  {{ t('ACTIVE_MESSAGES.HEADER_MEDIA') }}
                </h3>
                <Switch
                  :model-value="form.useHeader"
                  :aria-label="t('ACTIVE_MESSAGES.USE_HEADER_MEDIA')"
                  @update:model-value="toggleMediaHeader"
                />
              </div>
              <div v-if="form.useHeader" class="flex flex-col gap-2 mt-2">
                <div class="flex gap-2 items-start">
                  <div class="relative flex-shrink-0 w-32">
                    <select
                      id="active-message-header-type"
                      v-model="form.headerType"
                      :aria-label="t('ACTIVE_MESSAGES.HEADER_MEDIA_TYPE')"
                      class="appearance-none bg-none block w-full reset-base text-sm !mb-0 outline outline-1 border-none border-0 outline-offset-[-1px] rounded-lg bg-n-alpha-black2 py-[9px] pl-3 pr-10 outline-n-weak focus:outline-n-brand text-n-slate-12 transition-all duration-500 ease-in-out cursor-pointer"
                    >
                      <option value="image">
                        {{ t('ACTIVE_MESSAGES.IMAGE') }}
                      </option>
                      <option value="video">
                        {{ t('ACTIVE_MESSAGES.VIDEO') }}
                      </option>
                      <option value="document">
                        {{ t('ACTIVE_MESSAGES.DOCUMENT') }}
                      </option>
                    </select>
                    <div
                      class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none"
                    >
                      <div
                        class="i-lucide-chevron-down size-4 text-n-slate-11"
                      />
                    </div>
                  </div>
                  <div class="flex gap-2 flex-1">
                    <Input
                      v-model="form.headerUrl"
                      class="flex-1 !mb-0"
                      :aria-label="t('ACTIVE_MESSAGES.HEADER_MEDIA_URL')"
                      :placeholder="
                        t('ACTIVE_MESSAGES.HEADER_MEDIA_URL_PLACEHOLDER')
                      "
                    />
                    <Button
                      variant="outline"
                      :title="t('ACTIVE_MESSAGES.UPLOAD')"
                      :aria-label="t('ACTIVE_MESSAGES.UPLOAD_HEADER_MEDIA')"
                      @click="triggerHeaderUpload"
                    >
                      <div class="i-lucide-upload" />
                    </Button>
                    <input
                      ref="headerUploadInput"
                      type="file"
                      class="hidden"
                      :accept="
                        form.headerType === 'image'
                          ? 'image/jpeg,image/png,image/webp'
                          : form.headerType === 'video'
                            ? 'video/mp4'
                            : 'application/pdf'
                      "
                      @change="handleHeaderUpload"
                    />
                  </div>
                </div>
              </div>
            </div>
          </template>

          <div class="space-y-1">
            <TextArea
              id="active-message-content"
              v-model="form.content"
              :label="t('ACTIVE_MESSAGES.MAIN_MESSAGE')"
              :placeholder="t('ACTIVE_MESSAGES.MAIN_MESSAGE_PLACEHOLDER')"
              rows="5"
            />
            <Input
              v-model="form.footer"
              :label="t('ACTIVE_MESSAGES.FOOTER_MESSAGE')"
              :placeholder="t('ACTIVE_MESSAGES.FOOTER_MESSAGE_PLACEHOLDER')"
              maxlength="60"
              class="!mt-3"
            />
            <div
              class="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900/50 p-2 rounded-lg border border-slate-100 mt-2"
            >
              <div class="i-lucide-info text-blue-500" />
              <span
                ><strong>{{ t('ACTIVE_MESSAGES.DYNAMIC_VARIABLES') }}:</strong>
                {{ t('ACTIVE_MESSAGES.DYNAMIC_VARIABLES_DESCRIPTION') }}
                <code v-pre>{{ variavel }}</code></span
              >
            </div>
            <!-- Ações de pagamento -->
            <div
              v-if="form.format === 'payment'"
              class="p-4 border border-slate-200 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-900/50 relative mt-4"
            >
              <div class="flex justify-between items-center mb-3">
                <h3
                  class="font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-2"
                >
                  <div class="i-lucide-mouse-pointer-click" />
                  {{ t('ACTIVE_MESSAGES.PAYMENT_ACTIONS') }}
                </h3>
                <Button
                  v-if="form.paymentMethods.length === 0"
                  size="xs"
                  variant="ghost"
                  icon="i-lucide-plus"
                  :label="t('ACTIVE_MESSAGES.ADD')"
                  @click="addPaymentButton"
                />
              </div>
              <p
                v-if="!form.buttons.length"
                class="text-sm text-slate-500 dark:text-slate-400 mb-2"
              >
                {{ t('ACTIVE_MESSAGES.SELECT_PAYMENT_OR_ADD_ACTION') }}
              </p>
              <div
                v-for="(btn, i) in form.buttons"
                :key="`payment-${btn.payment_method}-${i}`"
                class="flex flex-col gap-2 mb-2 bg-white dark:bg-slate-800 p-3 rounded-lg border border-slate-200 dark:border-slate-700"
              >
                <div class="flex items-center gap-2">
                  <div
                    :class="
                      paymentMethodOptions.find(
                        item => item.key === btn.payment_method
                      )?.icon
                    "
                    class="size-5 shrink-0 text-indigo-500"
                  />
                  <select
                    v-model="btn.payment_method"
                    class="flex-1 min-w-0 appearance-none bg-none block reset-base text-sm !mb-0 outline outline-1 border-none border-0 outline-offset-[-1px] rounded-lg bg-n-alpha-black2 py-[9px] pl-3 pr-10 outline-n-weak focus:outline-n-brand text-n-slate-12"
                    :aria-label="`Tipo de pagamento ${i + 1}`"
                    @change="
                      btn.type = 'payment_method';
                      btn.actionType = 'payment';
                      btn.label = 'Pedir pagamento';
                    "
                  >
                    <option
                      v-for="method in paymentActionOptions"
                      :key="method.key"
                      :value="method.key"
                    >
                      {{ method.label }}
                    </option>
                  </select>
                  <Button
                    size="xs"
                    variant="ghost"
                    color="ruby"
                    icon="i-lucide-trash"
                    :aria-label="
                      t('ACTIVE_MESSAGES.ARIA.REMOVE_PAYMENT_ACTION', {
                        action: btn.payment_method,
                      })
                    "
                    @click="removePaymentButton(i)"
                  />
                </div>
                <div class="flex flex-col gap-1.5">
                  <label
                    :for="`payment-action-key-${i}`"
                    class="text-xs font-medium text-slate-600 dark:text-slate-300"
                  >
                    {{
                      btn.payment_method === 'pix'
                        ? t('ACTIVE_MESSAGES.PIX_CODE_OR_VARIABLE')
                        : btn.payment_method === 'boleto'
                          ? t('ACTIVE_MESSAGES.BOLETO_CODE_OR_VARIABLE')
                          : t('ACTIVE_MESSAGES.PAYMENT_LINK_OR_VARIABLE')
                    }}
                  </label>
                  <Input
                    :id="`payment-action-key-${i}`"
                    v-model="btn.url"
                    class="w-full !mb-0"
                    :aria-label="`Dados do pagamento ${i + 1}`"
                    :placeholder="
                      btn.payment_method === 'pix'
                        ? t('ACTIVE_MESSAGES.PIX_VALUE_PLACEHOLDER')
                        : btn.payment_method === 'boleto'
                          ? t('ACTIVE_MESSAGES.BOLETO_VALUE_PLACEHOLDER')
                          : t('ACTIVE_MESSAGES.PAYMENT_LINK_VALUE_PLACEHOLDER')
                    "
                  />
                </div>
                <div
                  v-if="btn.payment_method === 'pix'"
                  class="grid min-w-0 grid-cols-1 md:grid-cols-3 gap-2"
                >
                  <div class="min-w-0">
                    <Input
                      v-model="btn.merchant_name"
                      class="!mb-0 min-w-0"
                      :label="t('ACTIVE_MESSAGES.MERCHANT_NAME')"
                      :placeholder="
                        t('ACTIVE_MESSAGES.MERCHANT_NAME_PLACEHOLDER')
                      "
                    />
                  </div>
                  <div class="min-w-0">
                    <Input
                      v-model="btn.pix_key"
                      class="!mb-0 min-w-0"
                      :label="t('ACTIVE_MESSAGES.PIX_KEY')"
                      :placeholder="t('ACTIVE_MESSAGES.PIX_KEY_PLACEHOLDER')"
                    />
                  </div>
                  <div class="relative flex min-w-0 flex-col gap-1">
                    <label
                      :for="`payment-pix-key-type-${i}`"
                      class="mb-0.5 text-heading-3 !font-medium !leading-[21px] !text-n-slate-12"
                    >
                      {{ t('ACTIVE_MESSAGES.PIX_KEY_TYPE') }}
                    </label>
                    <select
                      :id="`payment-pix-key-type-${i}`"
                      v-model="btn.pix_key_type"
                      class="block h-10 min-w-0 w-full appearance-none bg-none reset-base text-sm !mb-0 outline outline-1 border-none border-0 outline-offset-[-1px] rounded-lg bg-n-alpha-black2 px-3 py-2.5 outline-n-weak focus:outline-n-brand text-n-slate-12"
                    >
                      <option value="CNPJ">
                        {{ t('ACTIVE_MESSAGES.PIX_KEY_TYPES.CNPJ') }}
                      </option>
                      <option value="CPF">
                        {{ t('ACTIVE_MESSAGES.PIX_KEY_TYPES.CPF') }}
                      </option>
                      <option value="EMAIL">
                        {{ t('ACTIVE_MESSAGES.PIX_KEY_TYPES.EMAIL') }}
                      </option>
                      <option value="PHONE">
                        {{ t('ACTIVE_MESSAGES.PIX_KEY_TYPES.PHONE') }}
                      </option>
                      <option value="RANDOM">
                        {{ t('ACTIVE_MESSAGES.PIX_KEY_TYPES.RANDOM') }}
                      </option>
                    </select>
                    <div
                      class="pointer-events-none absolute bottom-0 right-0 flex h-10 items-center pr-3 text-n-slate-11"
                    >
                      <div class="i-lucide-chevron-down size-4" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Botões de Ação -->
            <div
              v-if="
                ![
                  'payment',
                  'list',
                  'carousel',
                  'catalog',
                  'coupon',
                  'authentication',
                  'flow',
                ].includes(form.format)
              "
              class="p-4 border border-slate-200 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-900/50 relative mt-4"
            >
              <div class="flex justify-between items-center mb-3">
                <h3
                  class="font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-2"
                >
                  <div class="i-lucide-mouse-pointer-click" />
                  {{
                    form.format === 'quick_replies'
                      ? t('ACTIVE_MESSAGES.FORMAT_QUICK_REPLIES')
                      : form.format === 'personalized'
                        ? t('ACTIVE_MESSAGES.FORMAT_CUSTOM')
                        : t('ACTIVE_MESSAGES.RESPONSE_ACTION_BUTTONS')
                  }}
                </h3>
                <Button
                  v-if="form.buttons.length < MAX_TEMPLATE_BUTTONS"
                  size="xs"
                  variant="ghost"
                  icon="i-lucide-plus"
                  :label="t('ACTIVE_MESSAGES.ADD')"
                  @click="addButton"
                />
              </div>
              <div
                v-if="!form.buttons.length"
                class="text-sm text-slate-500 dark:text-slate-400 mb-2"
              >
                {{
                  form.format === 'quick_replies'
                    ? t('ACTIVE_MESSAGES.QUICK_REPLIES_DESCRIPTION')
                    : form.format === 'personalized'
                      ? t('ACTIVE_MESSAGES.BUTTONS_COMBINED_DESCRIPTION')
                      : t('ACTIVE_MESSAGES.BUTTONS_DESCRIPTION')
                }}
              </div>
              <div
                v-for="(btn, i) in form.buttons"
                :key="i"
                class="flex flex-col gap-2 mb-3 bg-white dark:bg-slate-800 p-3 rounded-lg border border-slate-200 dark:border-slate-700"
              >
                <div class="flex gap-2 items-start">
                  <div
                    v-if="form.format !== 'quick_replies'"
                    class="relative flex-shrink-0 w-32"
                  >
                    <select
                      v-model="btn.actionType"
                      :aria-label="
                        t('ACTIVE_MESSAGES.ARIA.BUTTON_TYPE', { number: i + 1 })
                      "
                      class="appearance-none bg-none block w-full reset-base text-sm !mb-0 outline outline-1 border-none border-0 outline-offset-[-1px] rounded-lg bg-n-alpha-black2 py-[9px] pl-3 pr-10 outline-n-weak focus:outline-n-brand text-n-slate-12 transition-all duration-500 ease-in-out cursor-pointer"
                      @change="handleButtonTypeChange(btn, $event)"
                    >
                      <option
                        value="reply"
                        :disabled="!canSelectButtonType('reply', i)"
                      >
                        {{
                          t('ACTIVE_MESSAGES.BUTTON_REPLY', {
                            limit: buttonTypeLimit('reply'),
                          })
                        }}
                      </option>
                      <option
                        value="url"
                        :disabled="!canSelectButtonType('url', i)"
                      >
                        {{
                          t('ACTIVE_MESSAGES.BUTTON_URL', {
                            limit: buttonTypeLimit('url'),
                          })
                        }}
                      </option>
                      <option
                        value="phone"
                        :disabled="!canSelectButtonType('phone', i)"
                      >
                        {{
                          t('ACTIVE_MESSAGES.BUTTON_PHONE', {
                            limit: buttonTypeLimit('phone'),
                          })
                        }}
                      </option>
                      <option value="payment">
                        {{ t('ACTIVE_MESSAGES.REQUEST_PAYMENT') }}
                      </option>
                    </select>
                    <div
                      class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none"
                    >
                      <div
                        class="i-lucide-chevron-down size-4 text-n-slate-11"
                      />
                    </div>
                  </div>
                  <select
                    v-if="btn.payment_method"
                    v-model="btn.payment_method"
                    class="flex-1 min-w-0 appearance-none bg-none block reset-base text-sm !mb-0 outline outline-1 border-none border-0 outline-offset-[-1px] rounded-lg bg-n-alpha-black2 py-[9px] pl-3 pr-10 outline-n-weak focus:outline-n-brand text-n-slate-12"
                    :aria-label="`Tipo de pagamento ${i + 1}`"
                  >
                    <option
                      v-for="method in paymentActionOptions"
                      :key="method.key"
                      :value="method.key"
                    >
                      {{ method.label }}
                    </option>
                  </select>
                  <Input
                    v-else
                    v-model="btn.label"
                    class="flex-1 !mb-0"
                    :aria-label="
                      t('ACTIVE_MESSAGES.BUTTON_TEXT_ARIA', { index: i + 1 })
                    "
                    :placeholder="t('ACTIVE_MESSAGES.BUTTON_TEXT')"
                    maxlength="40"
                  />
                  <Button
                    size="xs"
                    variant="ghost"
                    color="ruby"
                    icon="i-lucide-trash"
                    class="mt-1"
                    :aria-label="
                      t('ACTIVE_MESSAGES.REMOVE_BUTTON_ARIA', { index: i + 1 })
                    "
                    @click="form.buttons.splice(i, 1)"
                  />
                </div>
                <div
                  v-if="btn.payment_method"
                  class="pl-[136px] pr-8 flex flex-col gap-1.5"
                >
                  <label
                    :for="`payment-key-${i}`"
                    class="text-xs font-medium text-slate-600 dark:text-slate-300"
                  >
                    {{
                      btn.payment_method === 'pix'
                        ? t('ACTIVE_MESSAGES.PIX_CODE_OR_VARIABLE')
                        : btn.payment_method === 'boleto'
                          ? t('ACTIVE_MESSAGES.BOLETO_CODE_OR_VARIABLE')
                          : t('ACTIVE_MESSAGES.PAYMENT_LINK_OR_VARIABLE')
                    }}
                  </label>
                  <Input
                    :id="`payment-key-${i}`"
                    v-model="btn.url"
                    class="w-full !mb-0"
                    :aria-label="`Dados do pagamento ${i + 1}`"
                    :placeholder="
                      btn.payment_method === 'pix'
                        ? t('ACTIVE_MESSAGES.PIX_VALUE_PLACEHOLDER')
                        : btn.payment_method === 'boleto'
                          ? t('ACTIVE_MESSAGES.BOLETO_VALUE_PLACEHOLDER')
                          : t('ACTIVE_MESSAGES.PAYMENT_LINK_VALUE_PLACEHOLDER')
                    "
                  />
                  <div
                    v-if="btn.payment_method === 'pix'"
                    class="grid grid-cols-1 md:grid-cols-3 gap-2"
                  >
                    <Input
                      v-model="btn.merchant_name"
                      class="!mb-0"
                      :label="t('ACTIVE_MESSAGES.MERCHANT_NAME')"
                      :placeholder="
                        t('ACTIVE_MESSAGES.MERCHANT_NAME_PLACEHOLDER')
                      "
                    />
                    <Input
                      v-model="btn.pix_key"
                      class="!mb-0"
                      :label="t('ACTIVE_MESSAGES.PIX_KEY')"
                      :placeholder="t('ACTIVE_MESSAGES.PIX_KEY_PLACEHOLDER')"
                    />
                    <div class="relative flex min-w-0 flex-col gap-1">
                      <label
                        :for="`pix-key-type-${i}`"
                        class="mb-0.5 text-heading-3 !font-medium !leading-[21px] !text-n-slate-12"
                      >
                        {{ t('ACTIVE_MESSAGES.PIX_KEY_TYPE') }}
                      </label>
                      <select
                        :id="`pix-key-type-${i}`"
                        v-model="btn.pix_key_type"
                        class="block h-10 w-full appearance-none bg-none reset-base !mb-0 rounded-lg border-none border-0 bg-n-alpha-black2 px-3 py-2.5 text-sm text-n-slate-12 outline outline-1 outline-offset-[-1px] outline-n-weak focus:outline-n-brand"
                      >
                        <option value="CNPJ">
                          {{ t('ACTIVE_MESSAGES.PIX_KEY_TYPES.CNPJ') }}
                        </option>
                        <option value="CPF">
                          {{ t('ACTIVE_MESSAGES.PIX_KEY_TYPES.CPF') }}
                        </option>
                        <option value="EMAIL">
                          {{ t('ACTIVE_MESSAGES.PIX_KEY_TYPES.EMAIL') }}
                        </option>
                        <option value="PHONE">
                          {{ t('ACTIVE_MESSAGES.PIX_KEY_TYPES.PHONE') }}
                        </option>
                        <option value="RANDOM">
                          {{ t('ACTIVE_MESSAGES.PIX_KEY_TYPES.RANDOM') }}
                        </option>
                      </select>
                      <div
                        class="pointer-events-none absolute bottom-0 right-0 flex h-10 items-center pr-3 text-n-slate-11"
                      >
                        <div class="i-lucide-chevron-down size-4" />
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-else-if="btn.type === 'url' || btn.type === 'phone'"
                  class="pl-[136px] pr-8 flex flex-col gap-1.5"
                >
                  <label
                    :for="`button-target-${i}`"
                    class="text-xs font-medium text-slate-600 dark:text-slate-300"
                  >
                    {{
                      btn.type === 'phone'
                        ? t('ACTIVE_MESSAGES.PHONE_NUMBER')
                        : t('ACTIVE_MESSAGES.BUTTON_URL')
                    }}
                  </label>
                  <div class="flex items-center gap-2 w-full">
                    <select
                      v-if="btn.type === 'phone'"
                      v-model="btn.countryCode"
                      class="appearance-none bg-none block w-40 shrink-0 reset-base text-sm !mb-0 outline outline-1 border-none border-0 outline-offset-[-1px] rounded-lg bg-n-alpha-black2 py-[9px] px-3 outline-n-weak focus:outline-n-brand text-n-slate-12"
                      :aria-label="
                        t('ACTIVE_MESSAGES.ARIA.PHONE_COUNTRY', {
                          number: i + 1,
                        })
                      "
                      @change="handleButtonTypeChange(btn)"
                    >
                      <option
                        v-for="country in PHONE_COUNTRIES"
                        :key="country.code"
                        :value="country.code"
                      >
                        <!-- country.nameKey comes from the fixed PHONE_COUNTRIES catalog. -->
                        <!-- eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys -->
                        {{ t(country.nameKey) }} ({{ country.code }})
                      </option>
                    </select>
                    <Input
                      v-if="btn.type === 'phone'"
                      :id="`button-target-${i}`"
                      :model-value="formatPhoneLocalValue(btn)"
                      class="flex-1 min-w-0 !mb-0"
                      type="tel"
                      maxlength="15"
                      :placeholder="
                        t('ACTIVE_MESSAGES.PHONE_NUMBER_PLACEHOLDER')
                      "
                      @input="formatPhoneLocal(btn, $event)"
                    />
                    <Input
                      v-else
                      :id="`button-target-${i}`"
                      v-model="btn.url"
                      size="sm"
                      class="flex-1 min-w-0 w-full !mb-0"
                      :aria-label="
                        t('ACTIVE_MESSAGES.BUTTON_URL_ARIA', { index: i + 1 })
                      "
                      :placeholder="t('ACTIVE_MESSAGES.BUTTON_URL_PLACEHOLDER')"
                    />
                  </div>
                </div>
              </div>
            </div>

            <!-- Lista (Menu) -->
            <div
              v-if="form.format === 'list'"
              class="p-4 border border-slate-200 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-900/50 mt-4"
            >
              <div class="flex items-center justify-between gap-2 mb-3">
                <div class="flex items-center gap-2">
                  <div
                    class="i-lucide-list text-lg text-slate-800 dark:text-slate-200"
                  />
                  <h3 class="font-semibold text-slate-800 dark:text-slate-200">
                    {{ t('ACTIVE_MESSAGES.INTERACTIVE_MENU') }}
                  </h3>
                </div>
                <Button
                  v-if="form.listItems.length < 10"
                  size="xs"
                  variant="ghost"
                  icon="i-lucide-plus"
                  :label="t('ACTIVE_MESSAGES.ADD_OPTION')"
                  @click="addListItem"
                />
              </div>
              <p class="text-xs text-slate-500 dark:text-slate-400 mb-3">
                {{ t('ACTIVE_MESSAGES.MENU_DESCRIPTION') }}
              </p>
              <Input
                v-model="form.listButtonText"
                :label="t('ACTIVE_MESSAGES.MENU_BUTTON_TEXT')"
                :placeholder="t('ACTIVE_MESSAGES.MENU_BUTTON_PLACEHOLDER')"
                maxlength="20"
              />
              <div
                v-for="(item, i) in form.listItems"
                :key="i"
                class="flex flex-col gap-2 mb-3 bg-white dark:bg-slate-800 p-3 rounded-lg border border-slate-200 dark:border-slate-700"
              >
                <div class="flex items-start gap-2">
                  <div class="grid flex-1 grid-cols-1 gap-2 sm:grid-cols-2">
                    <Input
                      v-model="item.title"
                      :label="
                        t('ACTIVE_MESSAGES.OPTION_TITLE', { index: i + 1 })
                      "
                      :placeholder="
                        t('ACTIVE_MESSAGES.SETTLEMENT_BUTTON_EXAMPLE')
                      "
                      maxlength="24"
                      class="!mb-0"
                    />
                    <Input
                      v-model="item.description"
                      :label="
                        t('ACTIVE_MESSAGES.OPTION_DESCRIPTION', {
                          index: i + 1,
                        })
                      "
                      :placeholder="
                        t('ACTIVE_MESSAGES.OPTION_DESCRIPTION_PLACEHOLDER')
                      "
                      maxlength="72"
                      class="!mb-0"
                    />
                  </div>
                  <Button
                    size="xs"
                    variant="ghost"
                    color="ruby"
                    icon="i-lucide-trash"
                    :aria-label="
                      t('ACTIVE_MESSAGES.REMOVE_OPTION', { index: i + 1 })
                    "
                    class="mt-1"
                    @click="form.listItems.splice(i, 1)"
                  />
                </div>
              </div>
              <p
                v-if="!form.listItems.length"
                class="text-sm text-slate-500 dark:text-slate-400"
              >
                {{ t('ACTIVE_MESSAGES.ADD_OPTION_TO_SAVE_MENU') }}
              </p>
            </div>

            <!-- Catálogo -->
            <div
              v-if="form.format === 'catalog'"
              class="p-4 border border-slate-200 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-900/50 mt-4"
            >
              <div class="flex items-center gap-2 mb-3">
                <div
                  class="i-lucide-store text-lg text-slate-800 dark:text-slate-200"
                />
                <h3 class="font-semibold text-slate-800 dark:text-slate-200">
                  {{ t('ACTIVE_MESSAGES.CATALOG_BUTTON_TITLE') }}
                </h3>
              </div>
              <Input
                v-model="form.catalogId"
                :label="t('ACTIVE_MESSAGES.CATALOG_ID_LABEL')"
                :placeholder="t('ACTIVE_MESSAGES.CATALOG_ID_EXAMPLE')"
              />
              <p class="text-xs text-slate-500 mt-1">
                {{ t('ACTIVE_MESSAGES.CATALOG_ID_HELP') }}
              </p>
            </div>

            <!-- Oferta / Cupom -->
            <div
              v-if="form.format === 'coupon'"
              class="p-4 border border-slate-200 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-900/50 mt-4"
            >
              <div class="flex items-center gap-2 mb-3">
                <div
                  class="i-lucide-ticket text-lg text-slate-800 dark:text-slate-200"
                />
                <h3 class="font-semibold text-slate-800 dark:text-slate-200">
                  {{ t('ACTIVE_MESSAGES.COUPON_BUTTON_TITLE') }}
                </h3>
              </div>
              <Input
                v-model="form.couponCode"
                :label="t('ACTIVE_MESSAGES.COUPON_CODE_LABEL')"
                :placeholder="t('ACTIVE_MESSAGES.COUPON_CODE_EXAMPLE')"
                maxlength="15"
              />
              <p class="text-xs text-slate-500 mt-1">
                {{ t('ACTIVE_MESSAGES.COUPON_CODE_DESCRIPTION') }}
              </p>
            </div>

            <!-- Autenticação (OTP) -->
            <div
              v-if="form.format === 'authentication'"
              class="p-4 border border-slate-200 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-900/50 mt-4"
            >
              <div class="flex items-center gap-2 mb-3">
                <div
                  class="i-lucide-shield-check text-lg text-slate-800 dark:text-slate-200"
                />
                <h3 class="font-semibold text-slate-800 dark:text-slate-200">
                  {{ t('ACTIVE_MESSAGES.AUTH_BUTTON_TITLE') }}
                </h3>
              </div>
              <Input
                v-model="form.tokenCode"
                :label="t('ACTIVE_MESSAGES.AUTH_CODE_LABEL')"
                :placeholder="t('ACTIVE_MESSAGES.AUTH_CODE_EXAMPLE')"
              />
              <Input
                v-model="form.codeExpirationMinutes"
                type="number"
                :label="t('ACTIVE_MESSAGES.AUTH_EXPIRATION_LABEL')"
                min="1"
                max="90"
              />
              <p class="text-xs text-slate-500 mt-1">
                {{ t('ACTIVE_MESSAGES.AUTH_CODE_DESCRIPTION') }}
              </p>
            </div>

            <div
              v-if="form.format === 'flow'"
              class="p-4 border border-slate-200 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-900/50 mt-4 space-y-3"
            >
              <div class="flex items-center gap-2">
                <div class="i-lucide-workflow text-lg" />
                <h3 class="font-semibold">
                  {{ t('ACTIVE_MESSAGES.OFFICIAL_WHATSAPP_FLOW') }}
                </h3>
              </div>
              <p class="text-xs text-slate-500">
                {{ t('ACTIVE_MESSAGES.FLOW_DESCRIPTION') }}
              </p>
              <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Input
                  v-model="form.flowId"
                  :label="t('ACTIVE_MESSAGES.FLOW_ID')"
                />
                <Input
                  v-model="form.flowName"
                  :label="t('ACTIVE_MESSAGES.FLOW_NAME')"
                />
                <Input
                  v-model="form.flowCta"
                  :label="t('ACTIVE_MESSAGES.BUTTON_TEXT')"
                  maxlength="20"
                />
                <Input
                  v-model="form.flowScreen"
                  :label="t('ACTIVE_MESSAGES.FLOW_START_SCREEN')"
                />
              </div>
              <Input
                v-model="form.flowToken"
                :label="t('ACTIVE_MESSAGES.FLOW_TOKEN')"
                :placeholder="t('ACTIVE_MESSAGES.FLOW_TOKEN_EXAMPLE')"
              />
              <div
                class="rounded-lg border border-emerald-200 bg-emerald-50 p-3 dark:border-emerald-900 dark:bg-emerald-950/30"
              >
                <div class="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div
                      class="text-sm font-semibold text-emerald-900 dark:text-emerald-100"
                    >
                      {{ t('ACTIVE_MESSAGES.FLOW_VISUAL_EXPERIENCE') }}
                    </div>
                    <p
                      class="mt-1 text-xs text-emerald-800 dark:text-emerald-200"
                    >
                      {{ t('ACTIVE_MESSAGES.FLOW_VISUAL_DESCRIPTION') }}
                    </p>
                  </div>
                  <Button
                    color="slate"
                    size="sm"
                    icon="i-lucide-upload"
                    :label="t('ACTIVE_MESSAGES.IMPORT_JSON')"
                    @click="flowJsonInput?.click()"
                  />
                  <input
                    ref="flowJsonInput"
                    type="file"
                    accept=".json,application/json"
                    class="hidden"
                    @change="handleFlowJsonUpload"
                  />
                </div>
                <TextArea
                  id="active-message-flow-json"
                  v-model="flowJsonSource"
                  class="mt-3 font-mono"
                  :label="t('ACTIVE_MESSAGES.FLOW_JSON')"
                  placeholder='{"version":"7.1","screens":[...]}'
                  :rows="8"
                />
                <div class="mt-3 flex flex-wrap items-center gap-2">
                  <Button
                    color="blue"
                    size="sm"
                    icon="i-lucide-badge-check"
                    :label="t('ACTIVE_MESSAGES.VALIDATE_AND_APPLY')"
                    @click="applyFlowJson(flowJsonSource)"
                  />
                  <span
                    v-if="form.flowJsonFileName"
                    class="text-xs text-slate-500"
                  >
                    {{ form.flowJsonFileName }}
                  </span>
                </div>
                <div
                  v-if="flowJsonError"
                  role="alert"
                  class="mt-3 rounded-md bg-red-100 p-2 text-xs text-red-800 dark:bg-red-950/40 dark:text-red-200"
                >
                  {{ flowJsonError }}
                </div>
                <div
                  v-if="flowJsonSummary"
                  class="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-700 dark:text-slate-200 sm:grid-cols-5"
                >
                  <div class="rounded-md bg-white p-2 dark:bg-slate-900">
                    <strong>{{ t('ACTIVE_MESSAGES.SCHEMA') }}</strong
                    ><br />v{{ flowJsonSummary.version }}
                  </div>
                  <div class="rounded-md bg-white p-2 dark:bg-slate-900">
                    <strong>{{ t('ACTIVE_MESSAGES.SCREENS') }}</strong
                    ><br />{{ flowJsonSummary.screens }}
                  </div>
                  <div class="rounded-md bg-white p-2 dark:bg-slate-900">
                    <strong>{{ t('ACTIVE_MESSAGES.COMPONENTS') }}</strong
                    ><br />{{ flowJsonSummary.components }}
                  </div>
                  <div class="rounded-md bg-white p-2 dark:bg-slate-900">
                    <strong>{{ t('ACTIVE_MESSAGES.TERMINAL_SCREENS') }}</strong
                    ><br />{{ flowJsonSummary.terminalScreens }}
                  </div>
                  <div class="rounded-md bg-white p-2 dark:bg-slate-900">
                    <strong>{{ t('ACTIVE_MESSAGES.DATA_API') }}</strong
                    ><br />{{
                      flowJsonSummary.dataApiVersion ||
                      t('ACTIVE_MESSAGES.NO_ENDPOINT')
                    }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Carrossel -->
          <div
            v-if="form.format === 'carousel'"
            class="p-4 border border-slate-200 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-900/50 relative mt-4"
          >
            <div class="flex items-center mb-3">
              <h3
                class="font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-2"
              >
                <div class="i-lucide-gallery-horizontal-end" />
                {{ t('ACTIVE_MESSAGES.DYNAMIC_CAROUSEL') }}
              </h3>
            </div>
            <div class="space-y-4">
              <div
                v-for="(item, i) in form.carouselItems"
                :key="i"
                class="p-3 border border-indigo-100 bg-white dark:bg-slate-800 rounded-lg flex gap-3 relative group"
              >
                <div
                  class="w-24 flex-shrink-0 flex items-center justify-center relative"
                >
                  <div
                    class="w-20 h-20 bg-slate-100 dark:bg-slate-700 rounded flex items-center justify-center border border-dashed border-slate-300 overflow-hidden relative"
                  >
                    <img
                      v-if="item.imageUrl"
                      :src="item.imageUrl"
                      class="w-full h-full object-cover"
                    />
                    <div
                      v-else
                      class="i-lucide-image text-2xl text-slate-400"
                    />
                  </div>
                </div>
                <div class="flex-1 flex flex-col gap-2">
                  <div class="flex gap-2">
                    <Input
                      v-model="item.imageUrl"
                      size="sm"
                      class="flex-1 !mb-0"
                      :aria-label="
                        t('ACTIVE_MESSAGES.CAROUSEL_IMAGE_URL_ARIA', {
                          index: i + 1,
                        })
                      "
                      :placeholder="
                        t('ACTIVE_MESSAGES.CAROUSEL_IMAGE_URL_PLACEHOLDER')
                      "
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      :title="t('ACTIVE_MESSAGES.UPLOAD_IMAGE')"
                      :aria-label="
                        t('ACTIVE_MESSAGES.UPLOAD_CAROUSEL_IMAGE_ARIA', {
                          index: i + 1,
                        })
                      "
                      @click="triggerCarouselImageUpload(i)"
                    >
                      <div class="i-lucide-upload" />
                    </Button>
                  </div>
                  <Input
                    v-model="item.title"
                    size="sm"
                    class="!mb-0"
                    :aria-label="
                      t('ACTIVE_MESSAGES.ARIA.CARD_TITLE', { number: i + 1 })
                    "
                    :placeholder="t('ACTIVE_MESSAGES.CARD_TITLE_PLACEHOLDER')"
                    maxlength="60"
                  />
                  <Input
                    v-model="item.description"
                    size="sm"
                    class="!mb-0"
                    :aria-label="
                      t('ACTIVE_MESSAGES.ARIA.CARD_DESCRIPTION', {
                        number: i + 1,
                      })
                    "
                    :placeholder="
                      t('ACTIVE_MESSAGES.CARD_DESCRIPTION_PLACEHOLDER')
                    "
                    maxlength="60"
                  />
                  <Input
                    v-model="item.url"
                    size="sm"
                    class="!mb-0"
                    :aria-label="
                      t('ACTIVE_MESSAGES.ARIA.CARD_BUTTON_URL', {
                        number: i + 1,
                      })
                    "
                    :placeholder="
                      t('ACTIVE_MESSAGES.CARD_BUTTON_URL_PLACEHOLDER')
                    "
                  />
                  <Input
                    v-model="item.buttonText"
                    size="sm"
                    class="!mb-0"
                    :aria-label="
                      t('ACTIVE_MESSAGES.ARIA.CARD_BUTTON_TEXT', {
                        number: i + 1,
                      })
                    "
                    :placeholder="t('ACTIVE_MESSAGES.BUTTON_TEXT')"
                    maxlength="25"
                  />
                </div>
                <button
                  class="absolute -top-2 -right-2 bg-red-100 text-red-600 rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                  :aria-label="
                    t('ACTIVE_MESSAGES.ARIA.REMOVE_CARD', { number: i + 1 })
                  "
                  @click="form.carouselItems.splice(i, 1)"
                >
                  ×
                </button>
              </div>

              <Button
                size="sm"
                variant="outline"
                class="w-full"
                icon="i-lucide-plus"
                :label="t('ACTIVE_MESSAGES.ADD_CAROUSEL_CARD')"
                :disabled="form.carouselItems.length >= 10"
                @click="addCarouselItem"
              />
              <input
                ref="carouselUploadInput"
                type="file"
                class="hidden"
                accept="image/*"
                @change="handleCarouselImageUpload"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Preview Column -->
      <div
        class="flex h-1/2 min-h-0 w-full min-w-0 flex-none flex-col items-center overflow-x-hidden overflow-y-auto border-t border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900/50 xl:h-auto xl:w-[360px] xl:border-l xl:border-t-0"
      >
        <div class="flex w-full max-w-[320px] shrink-0 flex-col">
          <h3
            class="font-semibold text-slate-800 dark:text-slate-200 mb-3 flex items-center justify-center gap-2"
          >
            <div class="i-lucide-eye" />
            {{ t('ACTIVE_MESSAGES.LIVE_PREVIEW') }}
          </h3>

          <!-- Warning de API Não Oficial -->
          <div
            v-if="isEvolutionPreview"
            class="border border-slate-600 rounded-md p-3 flex items-center gap-3 mb-4 mt-2"
          >
            <div class="i-lucide-triangle-alert text-xl" />
            <div class="text-sm font-semibold text-center flex-1">
              {{ t('ACTIVE_MESSAGES.UNOFFICIAL_API_WARNING') }}
            </div>
          </div>

          <div
            class="flex bg-slate-200/50 dark:bg-slate-800/50 p-1 rounded-lg mb-4 w-full border border-slate-200 dark:border-slate-700"
            role="group"
            :aria-label="t('ACTIVE_MESSAGES.PREVIEW_MODE')"
          >
            <button
              type="button"
              :aria-pressed="previewApiMode === 'oficial'"
              class="flex-1 py-1.5 text-xs font-semibold rounded-md transition-all"
              :class="
                previewApiMode === 'oficial'
                  ? 'bg-white dark:bg-slate-700 text-slate-800 dark:text-white shadow-sm'
                  : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
              "
              @click="previewApiMode = 'oficial'"
            >
              {{ t('ACTIVE_MESSAGES.OFFICIAL_API') }}
            </button>
            <button
              type="button"
              :aria-pressed="previewApiMode === 'evolution'"
              class="flex-1 py-1.5 text-xs font-semibold rounded-md transition-all"
              :class="
                previewApiMode === 'evolution'
                  ? 'bg-white dark:bg-slate-700 text-slate-800 dark:text-white shadow-sm'
                  : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
              "
              :disabled="form.format === 'flow'"
              @click="previewApiMode = 'evolution'"
            >
              {{ t('ACTIVE_MESSAGES.UNOFFICIAL_API') }}
            </button>
          </div>
          <p
            v-if="selectedInbox"
            class="mb-4 text-center text-xs text-slate-500 dark:text-slate-400"
          >
            {{
              t('ACTIVE_MESSAGES.PREVIEW_SENDS_VIA', {
                api: isOfficialInbox
                  ? t('ACTIVE_MESSAGES.META_OFFICIAL_API')
                  : t('ACTIVE_MESSAGES.EVOLUTION_API'),
              })
            }}
            {{ t('ACTIVE_MESSAGES.PREVIEW_VISUAL_ONLY') }}
          </p>
        </div>

        <!-- MOCKUP CELULAR -->
        <div
          class="relative flex h-[540px] w-[300px] max-w-full shrink-0 flex-col overflow-hidden rounded-[2.5rem] border-[10px] border-black bg-black p-2 shadow-2xl xl:h-[560px] xl:w-[300px]"
        >
          <!-- Tela -->
          <div
            class="flex-1 bg-white rounded-[2rem] overflow-hidden flex flex-col relative"
          >
            <!-- Evolution Warning Removido -->
            <!-- Dynamic Island -->
            <div
              class="absolute top-2 left-1/2 -translate-x-1/2 w-24 h-6 bg-black rounded-full z-20"
            />

            <!-- Header -->
            <div
              class="bg-[#075E54] text-white pt-4 pb-3 px-4 flex items-center gap-3"
            >
              <div class="i-lucide-chevron-left text-2xl" />
              <div
                class="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center shadow-sm"
              >
                <div class="i-lucide-bot text-white" />
              </div>
              <div class="flex-1">
                <div
                  class="whitespace-nowrap font-semibold text-base leading-tight"
                >
                  {{ t('ACTIVE_MESSAGES.DEMO_COMPANY') }}
                </div>
                <div class="whitespace-nowrap text-[11px] text-white/80">
                  {{ t('ACTIVE_MESSAGES.DEMO_BUSINESS_ACCOUNT') }}
                </div>
              </div>
              <div class="i-lucide-phone text-xl" />
            </div>

            <!-- Chat Body -->
            <div
              class="flex-1 min-h-0 overflow-x-hidden overflow-y-auto bg-[#E5DDD5] p-3 space-y-4"
              style="
                background-image: url('https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png');
              "
            >
              <!-- Texto Base -->
              <div class="flex flex-col items-end">
                <div
                  class="w-full max-w-full bg-[#DCF8C6] p-1 rounded-xl rounded-tr-none shadow-sm text-[15px] text-slate-800 break-words whitespace-pre-wrap leading-snug"
                >
                  <!-- Mídia Header -->
                  <div
                    v-if="form.useHeader && form.category !== 'authentication'"
                    class="mb-1 rounded-lg overflow-hidden relative bg-black/5"
                    :class="
                      form.headerType === 'document'
                        ? 'h-12 flex items-center px-3 bg-black/10'
                        : 'h-32'
                    "
                  >
                    <img
                      v-if="form.headerType === 'image' && form.headerUrl"
                      :src="form.headerUrl"
                      class="w-full h-full object-cover"
                    />
                    <div
                      v-else-if="form.headerType === 'image'"
                      class="w-full h-full flex items-center justify-center"
                    >
                      <div class="i-lucide-image text-3xl text-slate-400" />
                    </div>
                    <div
                      v-else-if="form.headerType === 'video'"
                      class="w-full h-full flex items-center justify-center bg-slate-800"
                    >
                      <div
                        class="i-lucide-play-circle text-4xl text-white/80"
                      />
                    </div>
                    <div
                      v-else-if="form.headerType === 'document'"
                      class="w-full flex items-center gap-2"
                    >
                      <div
                        class="w-8 h-8 rounded bg-red-500 flex items-center justify-center"
                      >
                        <div class="i-lucide-file-text text-white" />
                      </div>
                      <span class="text-sm font-medium truncate flex-1">{{
                        form.headerUrl
                          ? t('ACTIVE_MESSAGES.ATTACHED_DOCUMENT')
                          : t('ACTIVE_MESSAGES.NO_DOCUMENT')
                      }}</span>
                    </div>
                  </div>

                  <div
                    v-if="form.paymentDetails"
                    class="mx-2 mt-2 overflow-hidden rounded-xl border border-slate-300 bg-white text-slate-700 shadow-[0_2px_8px_rgba(0,0,0,0.14)]"
                  >
                    <div
                      class="px-2.5 pt-2 text-[8px] uppercase tracking-[0.16em] text-slate-500"
                    >
                      {{
                        form.paymentTitle ||
                        t('ACTIVE_MESSAGES.PAYMENT_DETAILS')
                      }}
                    </div>
                    <div
                      class="mx-2.5 mt-1 border-b border-dashed border-slate-300 pb-2"
                    >
                      <div class="flex items-center gap-2">
                        <div
                          v-if="form.paymentImageUrl"
                          class="size-10 shrink-0 overflow-hidden rounded-lg border border-slate-200 bg-slate-100"
                        >
                          <img
                            :src="form.paymentImageUrl"
                            :alt="t('ACTIVE_MESSAGES.PAYMENT_ITEM_IMAGE')"
                            class="size-full object-cover"
                          />
                        </div>
                        <div
                          v-else
                          class="flex size-10 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-slate-100 text-slate-500"
                        >
                          <div class="i-lucide-receipt-text text-xl" />
                        </div>
                        <div class="min-w-0 flex-1">
                          <p class="truncate text-[11px] font-semibold">
                            {{
                              form.paymentProduct ||
                              t('ACTIVE_MESSAGES.PAYMENT_DEFAULT_PRODUCT')
                            }}
                          </p>
                          <p class="text-[9px] text-slate-500">
                            {{
                              t('ACTIVE_MESSAGES.PAYMENT_QUANTITY_VALUE', {
                                quantity: form.paymentQuantity || '1',
                              })
                            }}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div class="px-2.5 py-2 text-[9px]">
                      <div class="mt-1 flex items-center justify-between gap-2">
                        <span>{{ t('ACTIVE_MESSAGES.PAY_WITH') }}</span>
                        <span
                          class="flex items-center gap-1.5 font-medium"
                          :aria-label="
                            t('ACTIVE_MESSAGES.DISPLAYED_PAYMENT_METHODS')
                          "
                        >
                          <span
                            v-for="method in paymentMethodOptions.filter(item =>
                              form.paymentMethods.includes(item.key)
                            )"
                            :key="method.key"
                            :title="method.label"
                            :aria-label="method.label"
                            class="flex size-4 items-center justify-center text-slate-600"
                          >
                            <span :class="method.icon" class="size-3.5" />
                          </span>
                        </span>
                      </div>
                      <div
                        class="mt-2 flex items-center justify-between border-t border-slate-200 pt-1.5 font-semibold"
                      >
                        <span>{{ t('ACTIVE_MESSAGES.TOTAL') }}</span>
                        <span class="text-[10px]"
                          >R$ {{ paymentTotalPreview }}</span
                        >
                      </div>
                    </div>
                  </div>

                  <div class="px-2 pb-1 pt-1">
                    <template v-if="form.format === 'authentication'">
                      <div>
                        {{
                          t('ACTIVE_MESSAGES.AUTH_CODE_PREVIEW', {
                            code: form.tokenCode || defaultTokenCode,
                          })
                        }}
                      </div>
                      <div class="text-[13px] text-slate-600 mt-2">
                        {{ t('ACTIVE_MESSAGES.AUTH_SECURITY_PREVIEW') }}
                      </div>
                      <div class="text-[12px] text-slate-500 mt-2">
                        {{
                          t('ACTIVE_MESSAGES.AUTH_EXPIRES_AFTER', {
                            minutes: form.codeExpirationMinutes || 10,
                          })
                        }}
                      </div>
                    </template>
                    <template v-else>
                      {{
                        form.content ||
                        t('ACTIVE_MESSAGES.MAIN_MESSAGE_PREVIEW')
                      }}
                      <div
                        v-if="form.footer"
                        class="text-[13px] text-slate-500 mt-2"
                      >
                        {{ form.footer }}
                      </div>
                    </template>

                    <!-- Evolution Fallback Texts Inside Bubble -->
                    <template
                      v-if="isEvolutionPreview && form.format === 'list'"
                    >
                      <div class="font-bold mb-1">
                        *{{ t('ACTIVE_MESSAGES.AVAILABLE_OPTIONS') }}:*
                      </div>
                      <div v-for="(item, i) in form.listItems" :key="i">
                        ►
                        {{
                          item.title ||
                          t('ACTIVE_MESSAGES.OPTION_NUMBER', { number: i + 1 })
                        }}
                      </div>
                    </template>
                    <div
                      v-if="isEvolutionPreview"
                      class="mt-4 text-[13px] whitespace-pre-wrap font-mono opacity-80 text-slate-700"
                    >
                      <!-- Botões, Auth, Cupom, Catálogo -->
                      <template
                        v-if="
                          ['buttons', 'quick_replies', 'personalized'].includes(
                            form.format
                          )
                        "
                      >
                        <div class="font-bold mb-1">
                          *{{ t('ACTIVE_MESSAGES.AVAILABLE_OPTIONS') }}:*
                        </div>
                        <div v-for="(btn, i) in form.buttons" :key="i">
                          ► {{ btn.label || t('ACTIVE_MESSAGES.ACTION') }}
                        </div>
                      </template>
                      <template v-if="form.format === 'catalog'">
                        <div class="font-bold mb-1">
                          *{{ t('ACTIVE_MESSAGES.AVAILABLE_OPTIONS') }}:*
                        </div>
                        <div>► {{ t('ACTIVE_MESSAGES.VIEW_CATALOG') }}</div>
                      </template>
                      <template v-if="form.format === 'coupon'">
                        <div class="font-bold mb-1">
                          *{{ t('ACTIVE_MESSAGES.AVAILABLE_OPTIONS') }}:*
                        </div>
                        <div>► {{ t('ACTIVE_MESSAGES.COPY_COUPON') }}</div>
                      </template>
                      <template v-if="form.format === 'authentication'">
                        <div class="font-bold mb-1">
                          *{{ t('ACTIVE_MESSAGES.AVAILABLE_OPTIONS') }}:*
                        </div>
                        <div>► {{ t('ACTIVE_MESSAGES.COPY_CODE') }}</div>
                      </template>
                    </div>

                    <div class="text-[10px] text-slate-500 text-right mt-1">
                      {{ t('ACTIVE_MESSAGES.NOW') }}
                      <div
                        class="i-lucide-check-check text-[14px] text-blue-500 inline-block align-middle ml-1"
                      />
                    </div>
                  </div>
                </div>

                <!-- Lista (Menu) -->
                <div
                  v-if="form.format === 'list' && form.listItems.length"
                  class="flex flex-col gap-[2px] w-full max-w-[90%] mt-1"
                >
                  <div
                    class="bg-white text-[#00a884] text-center p-2.5 shadow-sm font-semibold text-[15px] rounded-t-lg"
                  >
                    <span class="flex items-center justify-center gap-2">
                      <div class="i-lucide-list text-lg" />
                      {{
                        form.listButtonText || t('ACTIVE_MESSAGES.VIEW_OPTIONS')
                      }}
                    </span>
                  </div>
                  <div
                    v-for="(item, i) in form.listItems"
                    :key="i"
                    class="bg-white text-slate-700 text-left px-3 py-2 shadow-sm"
                    :class="
                      i === form.listItems.length - 1 ? 'rounded-b-lg' : ''
                    "
                  >
                    <div class="font-semibold">
                      {{
                        item.title || `${t('ACTIVE_MESSAGES.OPTION')} ${i + 1}`
                      }}
                    </div>
                    <div v-if="item.description" class="text-xs text-slate-500">
                      {{ item.description }}
                    </div>
                  </div>
                </div>

                <!-- Botões -->
                <div
                  v-if="
                    form.buttons.length &&
                    ['buttons', 'quick_replies', 'personalized'].includes(
                      form.format
                    ) &&
                    !isEvolutionPreview
                  "
                  class="flex flex-col gap-[2px] w-full max-w-[90%] mt-1"
                >
                  <div
                    v-for="(btn, i) in form.buttons"
                    :key="i"
                    class="bg-white text-[#00a884] text-center p-2.5 shadow-sm font-semibold text-[15px] cursor-pointer"
                    :class="
                      i === 0
                        ? 'rounded-t-lg'
                        : i === form.buttons.length - 1
                          ? 'rounded-b-lg'
                          : ''
                    "
                  >
                    <span class="flex items-center justify-center gap-2">
                      <div
                        v-if="btn.type === 'url'"
                        class="i-lucide-external-link text-lg"
                      />
                      <div
                        v-else-if="btn.type === 'phone'"
                        class="i-lucide-phone text-lg"
                      />
                      <div v-else class="i-lucide-reply text-lg" />
                      {{ btn.label || t('ACTIVE_MESSAGES.ACTION') }}
                    </span>
                  </div>
                </div>

                <!-- Botão Único (Autenticação, Cupom, Catálogo, Flow) -->
                <div
                  v-if="
                    [
                      'authentication',
                      'coupon',
                      'payment',
                      'catalog',
                      'flow',
                    ].includes(form.format) && !isEvolutionPreview
                  "
                  class="flex w-full max-w-full flex-col gap-[2px] mt-1"
                >
                  <div
                    class="bg-white text-[#00a884] text-center p-2.5 shadow-sm font-semibold text-[15px] cursor-pointer rounded-lg border border-slate-200"
                    @click="
                      form.format === 'flow' &&
                        (isFlowPreviewOpen = !isFlowPreviewOpen)
                    "
                  >
                    <span class="flex items-center justify-center gap-2">
                      <div
                        v-if="form.format === 'catalog'"
                        class="i-lucide-store text-lg"
                      />
                      <div
                        v-else-if="form.format === 'flow'"
                        class="i-lucide-workflow text-lg"
                      />
                      <div v-else class="i-lucide-copy text-lg" />
                      {{
                        form.format === 'authentication'
                          ? t('ACTIVE_MESSAGES.COPY_CODE')
                          : form.format === 'coupon'
                            ? t('ACTIVE_MESSAGES.COPY_COUPON')
                            : form.format === 'payment'
                              ? paymentPrimaryActionLabel
                              : form.format === 'flow'
                                ? form.flowCta || t('ACTIVE_MESSAGES.OPEN')
                                : t('ACTIVE_MESSAGES.VIEW_CATALOG')
                      }}
                    </span>
                  </div>
                </div>

                <FlowJsonPreview
                  v-if="
                    form.format === 'flow' &&
                    isFlowPreviewOpen &&
                    !isEvolutionPreview &&
                    form.flowJson
                  "
                  :schema="form.flowJson"
                  :initial-screen-id="form.flowScreen"
                  @close="isFlowPreviewOpen = false"
                />
                <div
                  v-else-if="
                    form.format === 'flow' &&
                    isFlowPreviewOpen &&
                    !isEvolutionPreview
                  "
                  class="w-full max-w-[90%] rounded-xl border border-dashed border-slate-300 bg-white p-4 text-center text-xs text-slate-500"
                >
                  {{ t('ACTIVE_MESSAGES.FLOW_PREVIEW_DESCRIPTION') }}
                </div>
              </div>

              <!-- Carrossel Preview -->
              <div
                v-if="
                  form.format === 'carousel' &&
                  form.carouselItems.length &&
                  !isEvolutionPreview
                "
                class="flex gap-2 overflow-x-auto pb-2 snap-x"
              >
                <div
                  v-for="(item, i) in form.carouselItems"
                  :key="i"
                  class="min-w-[200px] bg-white rounded-xl shadow-sm overflow-hidden flex flex-col snap-center border border-slate-200"
                >
                  <div
                    class="h-24 bg-slate-200 flex items-center justify-center overflow-hidden"
                  >
                    <img
                      v-if="item.imageUrl"
                      :src="item.imageUrl"
                      class="w-full h-full object-cover"
                    />
                    <div
                      v-else
                      class="i-lucide-image text-3xl text-slate-400"
                    />
                  </div>
                  <div class="p-3 border-b border-slate-100">
                    <div
                      class="font-bold text-slate-800 text-[15px] leading-tight mb-1"
                    >
                      {{ item.title || t('ACTIVE_MESSAGES.CARD_TITLE') }}
                    </div>
                    <div class="text-[13px] text-slate-500 leading-snug">
                      {{
                        item.description ||
                        t('ACTIVE_MESSAGES.CARD_DESCRIPTION')
                      }}
                    </div>
                  </div>
                  <div
                    class="text-[#00a884] text-center p-2.5 font-semibold text-[15px]"
                  >
                    {{ item.buttonText || t('ACTIVE_MESSAGES.VIEW_MORE') }}
                  </div>
                </div>
              </div>

              <!-- Evolution Fallback for Carousel (Separate Bubbles) -->
              <div
                v-if="
                  form.format === 'carousel' &&
                  form.carouselItems.length &&
                  isEvolutionPreview
                "
                class="flex flex-col gap-4 items-end mt-2"
              >
                <div
                  v-for="(item, i) in form.carouselItems"
                  :key="'fb-' + i"
                  class="w-full max-w-full bg-[#DCF8C6] p-1 rounded-xl rounded-tr-none shadow-sm text-[15px] text-slate-800 break-words whitespace-pre-wrap leading-snug"
                >
                  <div
                    v-if="item.imageUrl"
                    class="mb-1 rounded-lg overflow-hidden h-32 bg-slate-200 flex items-center justify-center"
                  >
                    <img
                      :src="item.imageUrl"
                      class="w-full h-full object-cover"
                    />
                  </div>
                  <div class="px-2 pb-1 pt-1 text-[13px]">
                    <div
                      v-if="item.title"
                      class="font-bold mb-1 font-mono opacity-80 text-slate-700"
                    >
                      *{{ item.title }}*
                    </div>
                    <div
                      v-if="item.description"
                      class="mb-1 font-mono opacity-80 text-slate-700"
                    >
                      {{ item.description }}
                    </div>
                    <div
                      v-if="item.url"
                      class="font-mono opacity-80 text-slate-700"
                    >
                      ► {{ item.url }}
                    </div>
                    <div class="text-[10px] text-slate-500 text-right mt-1">
                      {{ t('ACTIVE_MESSAGES.NOW') }}
                      <div
                        class="i-lucide-check-check text-[14px] text-blue-500 inline-block align-middle ml-1"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer Buttons overlayed or inside? Let's put them absolute or below -->
    <div
      class="flex flex-col-reverse justify-end gap-3 border-t border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-900/50 sm:flex-row sm:justify-end rounded-b-xl -m-6 mt-0 relative z-10"
    >
      <Button
        variant="faded"
        color="slate"
        :label="t('COMMON.CANCEL')"
        type="button"
        class="w-full sm:w-auto"
        @click="handleClose"
      />
      <Button
        color="blue"
        :label="t('ACTIVE_MESSAGES.SAVE_TEMPLATE')"
        type="button"
        class="w-full sm:w-auto"
        :disabled="isSaving"
        :is-loading="isSaving"
        @click="handleSave"
      />
    </div>
  </Dialog>
</template>
